import { reconcileFishydexCatalog } from "../fishydex/catalog-renderer.js";
import { catalogKind, selectCatalogItems, selectFishProgressItems } from "../fishydex/catalog-contract.js";
import { mountFishDetailNavigation, readFishDetailSequence } from "../fishydex/detail-navigation.js";
import { pageTabForPath } from "../page-tab-routes.js";
import "../components/fishing-contest.js";
import { mountFishydexQuery } from "../fishydex/query-controller.js";
import { browserFishydexStateComposition } from "../fishydex/state-composition.js";
import { mountTitleControls } from "../fishydex/title-controls.js";
import { projectTitleProgress, projectTitleCaughtIds, fishGuideProjectionKey, fishProgressChanges } from "../fishydex/title-progress.js";
import {
  FISHYDEX_CATALOG_VIEWS,
  MAX_FISHYDEX_CATALOG_ITEMS,
  selectFishydexSurfaceProjection,
} from "../fishydex/surface-projection.js";

(function () {
  const FISHYDEX_PRESET_COLLECTION_KEY = "fishydex-presets";
  const FISHYDEX_HISTORY_STATE_KEY = "__fishystuffFishydex";
  const GRADE_COLOR_ORDER = ["red", "yellow", "blue", "green", "white", "unknown"];
  const METHOD_ORDER = ["rod", "hotspot", "harpoon"];
  const SORT_FIELD_ORDER = ["name", "price"];
  const SORT_DIRECTION_ORDER = ["asc", "desc"];
  const CATALOG_STATE_ORDER = ["pending", "ready", "unavailable"];
  const ZONE_EVIDENCE_KIND_ORDER = ["known", "potential"];
  const DEVICE_UI_INTENT_FIELDS = Object.freeze([
    "search_query",
    "show_non_fish",
    "sort_field",
    "sort_direction",
    "catalog_view",
  ]);
  const INITIAL_PRESENTATION = Object.freeze({
    lang: Object.freeze({ contentLang: "", locale: "", apiLang: "" }),
    fish: Object.freeze([]),
    revision: "",
    count: 0,
    search_query: "",
    show_dried: false,
    show_non_fish: false,
    sort_field: "price",
    sort_direction: "desc",
    catalog_view: "grade",
    _selected_fish_id: 0,
    catalog_count: 0,
    fish_count: 0,
    visible_count: 0,
    caught_count: 0,
    completion_percent: 0,
    red_total_count: 0,
    red_caught_count: 0,
    red_completion_percent: 0,
    yellow_total_count: 0,
    yellow_caught_count: 0,
    yellow_completion_percent: 0,
    blue_total_count: 0,
    blue_caught_count: 0,
    blue_completion_percent: 0,
    green_total_count: 0,
    green_caught_count: 0,
    green_completion_percent: 0,
    white_total_count: 0,
    white_caught_count: 0,
    white_completion_percent: 0,
    supports_grade_filter: true,
    supports_dried_filter: true,
    supports_guide_view: true,
    _catalog_state: "pending",
    _loading: true,
    _caught_stamp_fish_id: null,
    _favourite_stamp_fish_id: null,
    _show_dried_pending: false,
    _query_unavailable: false,
    _api_error_message: "",
    _api_error_hint: "",
  });
  const zoneEvidenceContract = window.__fishystuffZoneEvidenceContract;
  const normalizeZoneEvidenceResponse = zoneEvidenceContract?.normalizeResponse;
  const fishingGroupBadgeTone = zoneEvidenceContract?.fishingGroupBadgeTone;
  const fishingGroupMessageKey = zoneEvidenceContract?.fishingGroupMessageKey;
  const partitionZoneEvidence = zoneEvidenceContract?.partitionZoneEvidence;
  const rankingRingIconName = zoneEvidenceContract?.rankingRingIconName;
  const zoneEvidenceMapOperator = zoneEvidenceContract?.zoneEvidenceMapOperator;
  if (
    typeof normalizeZoneEvidenceResponse !== "function"
    || typeof fishingGroupBadgeTone !== "function"
    || typeof fishingGroupMessageKey !== "function"
    || typeof partitionZoneEvidence !== "function"
    || typeof rankingRingIconName !== "function"
    || typeof zoneEvidenceMapOperator !== "function"
  ) {
    throw new Error("Fishydex zone-evidence contract is unavailable");
  }
  const FISH_GUIDE_CATEGORY_ORDER = [
    "freshwater",
    "saltwater",
    "crustacean",
    "other",
    "unknown",
  ];
  // Derived from source-backed data/tables/excel/Encyclopedia_Table.xlsx Category by encyclopedia Key.
  // Index 0 in this string corresponds to encyclopedia key 1; '?' marks gaps in the source.
  const FISH_GUIDE_CATEGORY_SEQUENCE =
    "ssssfssssshhchcffsffffssssssssshhsssfsfffffhfcfffffffffssssssssssssssssssssssssssssssfffffffffffsfsssssssssssssssssssssssssssssssssshssssssshssssssssshssfffffffffffffffffffffffsssshssfshscsfffsscsssssssffffffffffffffffffffchsssshsssssfhf?sshshsfshfcsscsfhsshfsss?shcchsf?hfhsssffffffffffffffssssssssssfs";
  const SILVER_FORMATTER = new Intl.NumberFormat();
  const CATCH_COUNT_FORMATTER = new Intl.NumberFormat();
  const state = {
    lifecycleGeneration: 0,
    catalogAbortController: null,
    zoneEvidenceRequests: new Map(),
    composition: null,
    currentRepository: null,
    currentProjection: null,
    currentUnsubscribe: null,
    uiRepository: null,
    uiProjection: null,
    uiUnsubscribe: null,
    uiWritable: false,
    pageController: null,
    mountOwner: null,
    presentation: null,
    surface: null,
    uiStateRestored: false,
    fishById: new Map(),
    renderedCardStateByFish: new Map(),
    zoneEvidenceByFish: new Map(),
    zoneEvidenceTabByFish: new Map(),
    renderKey: "",
    cardContentKey: "",
    gridBound: false,
    detailsBound: false,
    historyBound: false,
    historyOwner: "",
    rootDocumentTitle: "",
    documentTitleSuffix: "",
    toastKeys: new Set(),
    suppressCardAnimation: false,
    detailsOpen: false,
    fishGuideProgress: { status: "unknown", snapshot: null },
    guideLookupKey: undefined,
    guideLookup: null,
    renderedCards: new Map(),
    catalogContractError: null,
    syncing: false,
    authoredHydrationRevision: 0,
  };

  function spriteIconMarkup(name, className, inline) {
    const classes = ["fishy-icon"];
    if (inline) {
      classes.push("fishy-icon--inline");
    }
    if (className) {
      classes.push(className);
    }
    return `<svg class="${classes.join(" ")}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${name}"></use></svg>`;
  }

  const ICON_HEART_FILL = spriteIconMarkup("heart-fill", "size-6", true);
  const ICON_HEART_LINE = spriteIconMarkup("heart-line", "size-6", true);
  const ICON_CAUGHT_FILL = spriteIconMarkup("check-badge-solid", "size-7", true);
  const ICON_CAUGHT_LINE = spriteIconMarkup("check-circle-dash-line", "size-7", true);
  const ICON_COIN_STACK = spriteIconMarkup("coin-stack", "", false);
  const ICON_TARGET_FILL = spriteIconMarkup("target-fill", "size-4", true);

  function languageHelper() {
    const helper = window.__fishystuffLanguage;
    return helper && typeof helper.current === "function" && typeof helper.t === "function"
      ? helper
      : null;
  }

  function languageReady() {
    const ready = languageHelper()?.ready;
    return ready && typeof ready.then === "function" ? ready.catch(() => {}) : Promise.resolve();
  }

  function t(key, vars = {}) {
    const helper = languageHelper();
    return helper ? helper.t(key, vars) : key;
  }

  function currentLanguage() {
    const helper = languageHelper();
    return helper ? helper.current() : {
      contentLang: "en-US",
      locale: "en-US",
      apiLang: "en",
    };
  }

  function languageText(key, vars = {}) {
    return t(key, vars);
  }

  function fishDisplayName(entry, fallbackId) {
    const name = String(entry && entry.name || "").trim();
    return name || languageText("domain.entity.fish_id", { id: fallbackId });
  }

  function fishDescription(value) {
    if (value == null) {
      return null;
    }
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      throw new TypeError("Fish description must be an object");
    }
    const source = String(value.source || "").trim();
    const text = String(value.text || "").trim();
    if (!["fish_guide", "knowledge", "item"].includes(source) || !text) {
      throw new TypeError("Fish description has an invalid source or text");
    }
    return { source, text };
  }

  function fishSlug(entry) {
    const slug = String(entry && entry.slug || "").trim();
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? slug : "";
  }

  function fishydexPageRoot() {
    const page = document.getElementById("page");
    return page instanceof HTMLElement ? page : null;
  }

  function fishydexBaseHref() {
    const configured = String(fishydexPageRoot()?.dataset.fishydexBaseHref || "").trim();
    return configured ? new URL(configured, window.location.href).toString() : "";
  }

  function zoneRgbPresentation(value) {
    const match = String(value || "").trim().match(/^(\d{1,3}),(\d{1,3}),(\d{1,3})$/);
    if (!match) {
      return null;
    }
    const channels = match.slice(1).map(Number);
    if (channels.some((channel) => channel > 255)) {
      return null;
    }
    return {
      cssValue: channels.join(" "),
      hex: channels.map((channel) => channel.toString(16).padStart(2, "0")).join(""),
    };
  }

  function fishydexMapHref(itemId, zone = null) {
    const configured = String(fishydexPageRoot()?.dataset.fishydexMapHref || "").trim();
    if (!configured || !Number.isInteger(itemId) || itemId <= 0) {
      return "";
    }
    let expression = `fish:${itemId}`;
    if (zone !== null) {
      const rgb = zoneRgbPresentation(zone.zone_rgb);
      if (!rgb) {
        return "";
      }
      expression += ` ${zoneEvidenceMapOperator(zone)} zone:0x${rgb.hex}`;
    }
    const href = new URL(configured, window.location.href);
    href.searchParams.set("expr", expression);
    return href.toString();
  }

  function fishDetailHref(entry) {
    const slug = fishSlug(entry);
    const baseHref = fishydexBaseHref();
    const helper = languageHelper();
    if (!slug || !baseHref || typeof helper?.resolveContentUrl !== "function") {
      return "";
    }
    const localizedPath = helper.resolveContentUrl(
      currentLanguage().contentLang,
      `/dex/${slug}/`,
    );
    try {
      const base = new URL(baseHref, window.location.href);
      const detail = new URL(localizedPath, window.location.href);
      return detail.origin === base.origin
        && detail.pathname !== base.pathname
        && detail.pathname.startsWith(base.pathname)
        ? detail.toString()
        : "";
    } catch (_error) {
      return "";
    }
  }

  function currentDataHtmlLang() {
    const mappings = window.__fishystuffGeneratedI18n?.config?.dataLanguageHtmlLangs;
    const apiLang = String(currentLanguage().apiLang || "").trim();
    return mappings && typeof mappings[apiLang] === "string" ? mappings[apiLang] : "";
  }

  function applyDataLanguage(element) {
    if (!(element instanceof HTMLElement)) {
      return;
    }
    const htmlLang = currentDataHtmlLang();
    if (htmlLang) {
      element.setAttribute("lang", htmlLang);
    } else {
      element.removeAttribute("lang");
    }
  }

  function hrefHasSameResource(leftHref, rightHref) {
    try {
      const left = new URL(leftHref, window.location.href);
      const right = new URL(rightHref, window.location.href);
      return left.origin === right.origin && left.pathname === right.pathname;
    } catch (_error) {
      return false;
    }
  }

  function fishydexRootIsCurrentResource() {
    const baseHref = fishydexBaseHref();
    return Boolean(baseHref) && pageTabForPath("dex", new URL(baseHref).pathname, new URL(window.location.href).pathname) !== null;
  }

  function documentTitleSuffix(value) {
    const title = String(value || "");
    const separatorIndex = title.indexOf(" | ");
    return separatorIndex >= 0 ? title.slice(separatorIndex) : "";
  }

  function restoreRootDocumentTitle() {
    if (state.rootDocumentTitle) {
      document.title = state.rootDocumentTitle;
    }
  }

  function setFishDocumentTitle(meta) {
    if (!meta || !meta.name) {
      return;
    }
    document.title = `${meta.name}${state.documentTitleSuffix}`;
  }

  function ownedHistoryFishId(historyState) {
    const marker = historyState && typeof historyState === "object"
      ? historyState[FISHYDEX_HISTORY_STATE_KEY]
      : null;
    const fishId = Number(marker && marker.fishId);
    return marker
      && marker.owner === state.historyOwner
      && Number.isInteger(fishId)
      && fishId > 0
      ? fishId
      : 0;
  }

  function currentHistoryFishId() {
    return ownedHistoryFishId(window.history && window.history.state);
  }

  function pushFishHistory(meta, href, mode = "push") {
    if (
      !meta
      || !href
      || !window.history
      || typeof window.history.pushState !== "function"
    ) {
      return false;
    }
    const currentState = window.history.state;
    const nextState = currentState && typeof currentState === "object" && !Array.isArray(currentState)
      ? { ...currentState }
      : {};
    nextState[FISHYDEX_HISTORY_STATE_KEY] = {
      owner: state.historyOwner,
      fishId: meta.fishId,
      returnHref: mode === "replace" ? currentState?.[FISHYDEX_HISTORY_STATE_KEY]?.returnHref ?? null : null,
    };
    try {
      if (mode === "replace") window.history.replaceState(nextState, "", href);
      else window.history.pushState(nextState, "", href);
    } catch (_error) {
      return false;
    }
    selectFishDetails(meta.fishId);
    return true;
  }

  function isUnmodifiedPrimaryClick(event) {
    return !event.defaultPrevented
      && (typeof event.button !== "number" || event.button === 0)
      && !event.metaKey
      && !event.ctrlKey
      && !event.shiftKey
      && !event.altKey;
  }

  function favouriteToggleLabel(fishName, isFavourite) {
    return languageText(
      isFavourite ? "fishydex.action.favourite.remove" : "fishydex.action.favourite.add",
      { name: fishName },
    );
  }

  function caughtToggleLabel(fishName, isCaught) {
    return languageText(
      isCaught ? "fishydex.action.caught.unmark" : "fishydex.action.caught.mark",
      { name: fishName },
    );
  }

  function sharedFishStateHelper() {
    const helper = window.__fishystuffSharedFishState;
    return helper && typeof helper.normalizeIds === "function" ? helper : null;
  }

  function requireStateComposition(candidate) {
    const composition = candidate ?? browserFishydexStateComposition();
    for (const [owner, methods] of [
      [composition?.current, [
        "activateFishGuideSnapshot",
        "load",
        "setCaught",
        "setFavourite",
        "setShowDried",
        "state",
        "subscribe",
      ]],
      [composition?.ui, ["load", "replace", "state", "subscribe"]],
    ]) {
      for (const method of methods) {
        if (typeof owner?.[method] !== "function") {
          throw new TypeError(`Fishydex state composition is missing ${method}()`);
        }
      }
    }
    return composition;
  }

  function repositoryStatusMessage(projection, domain = "progress") {
    if (projection?.kind === "unavailable") {
      return languageText("fishydex.state.local_storage_unavailable");
    }
    if (projection?.kind === "conflict") {
      return languageText("presets.state.conflict");
    }
    if (!["invalid", "unsupportedVersion"].includes(projection?.kind)) {
      return "";
    }
    if (domain === "guide") {
      return languageText("fishydex.state.corrupt_fish_guide");
    }
    if (domain === "ui") {
      return languageText("fishydex.state.corrupt_filters");
    }
    return languageText("fishydex.state.corrupt_progress");
  }

  function syncCurrentRepositoryStatus(projection) {
    const status = document.getElementById("fishydex-root-state-status");
    if (!(status instanceof HTMLElement)) {
      return;
    }
    const message = repositoryStatusMessage(projection);
    status.hidden = !message;
    status.textContent = message;
  }


  function guideCountForEntry(entry, projection = state.currentRepository?.state()) {
    const key = fishGuideProjectionKey(projection);
    if (!state.guideLookup || key !== state.guideLookupKey) {
      const helper = sharedFishStateHelper();
      state.guideLookup = helper.createFishGuideCountLookup(
        projection?.fishGuideProgress || { status: "unknown", snapshot: null },
      );
      state.guideLookupKey = key;
    }
    return state.guideLookup(fishGuideOrderForEntry(entry));
  }

  function fishGuideCatchCountForEntry(entry, projection = state.currentRepository?.state()) {
    return projectTitleProgress(entry, [], projection, guideCountForEntry(entry, projection)).count;
  }

  function effectiveCaughtIds(fish) {
    return projectTitleCaughtIds(
      fish, state.currentRepository?.state(), fishGuideCatchCountForEntry,
    );
  }

  function sharedFishSnapshot(repository = state.currentRepository) {
    const projection = repository?.state?.() ?? state.currentProjection ?? null;
    if (!projection || projection.catchCounts === null || projection.favouriteIds === null) {
      return null;
    }
    return {
      favouriteIds: normalizeStoredFishIds(projection.favouriteIds),
      showDried: projection.showDried,
    };
  }

  function clonePresentation(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function createInitialPresentation() {
    return clonePresentation(INITIAL_PRESENTATION);
  }

  function mountIsActive(owner) {
    return Boolean(
      owner
      && !owner.disposed
      && state.mountOwner === owner
      && state.lifecycleGeneration === owner.generation
      && state.surface === owner.surface
      && state.presentation === owner.presentation
    );
  }

  function presentationObject(owner = state.mountOwner) {
    return mountIsActive(owner) ? owner.presentation : null;
  }

  function listen(target, type, listener, options, owner = state.mountOwner) {
    target.addEventListener(type, listener, options);
    const cleanup = () => target.removeEventListener(type, listener, options);
    owner?.cleanups.push(cleanup);
    return cleanup;
  }

  function cancelPageRequests(generation = state.lifecycleGeneration) {
    if (state.catalogAbortController?.generation === generation) {
      state.catalogAbortController.controller.abort();
      state.catalogAbortController = null;
    }
    for (const [itemId, request] of state.zoneEvidenceRequests) {
      if (request.generation === generation) {
        request.controller.abort();
        state.zoneEvidenceRequests.delete(itemId);
      }
    }
  }

  function requestIsCurrent(request) {
    return request.generation === state.lifecycleGeneration
      && state.presentation !== null
      && !request.controller.signal.aborted;
  }

  function patchPresentation(patch, owner = state.mountOwner) {
    if (!mountIsActive(owner) || !patch || typeof patch !== "object" || Array.isArray(patch)) {
      return;
    }
    const ownedPatch = clonePresentation(patch);
    Object.assign(owner.presentation, ownedPatch);
    const surfacePatch = selectFishydexSurfaceProjection(ownedPatch);
    if (Object.keys(surfacePatch).length > 0) {
      owner.surface.applyProjection(surfacePatch);
    }
    if (owner.uiStateRestored && !state.syncing) {
      const presentation = presentationObject(owner);
      if (presentation) {
        sync(presentation);
      }
    }
  }

  function normalizeBooleanFlag(value) {
    return value === true || value === "true" || value === 1 || value === "1";
  }

  function requireExactFields(value, fields, label) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      throw new TypeError(`${label} must be an object`);
    }
    const actual = Object.keys(value).sort();
    const expected = fields.slice().sort();
    if (
      actual.length !== expected.length
      || actual.some((field, index) => field !== expected[index])
    ) {
      throw new TypeError(`${label} has unsupported or missing fields`);
    }
    return value;
  }

  function connectSurface(surface, presentation) {
    if (
      !(surface instanceof HTMLElement)
      || typeof surface.applyProjection !== "function"
    ) {
      throw new TypeError("Fishydex surface must expose applyProjection()");
    }
    surface.applyProjection(selectFishydexSurfaceProjection(presentation, { complete: true }));
  }

  function normalizeStoredFishIds(value) {
    const helper = sharedFishStateHelper();
    if (helper) {
      return helper.normalizeIds(value);
    }
    return [];
  }

  function normalizeMethod(value) {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "hotspot") {
      return "hotspot";
    }
    if (normalized === "harpoon") {
      return "harpoon";
    }
    if (normalized === "rod") {
      return "rod";
    }
    return "unknown";
  }

  function normalizeMethodList(values) {
    const rawValues = Array.isArray(values)
      ? values
      : values === undefined || values === null
        ? []
        : [values];
    const selected = new Set();
    for (const raw of rawValues) {
      const method = normalizeMethod(raw);
      if (method !== "unknown") {
        selected.add(method);
      }
    }
    return METHOD_ORDER.filter(function (method) {
      return selected.has(method);
    });
  }

  function validateEntryCatchMethods(entry, label) {
    const methods = entry && entry.catch_methods;
    if (!Array.isArray(methods)) {
      throw new TypeError(`${label}.catch_methods must be an array`);
    }
    const canonical = METHOD_ORDER.filter(function (method) {
      return methods.includes(method);
    });
    if (
      canonical.length !== methods.length
      || canonical.some(function (method, index) { return methods[index] !== method; })
    ) {
      throw new TypeError(
        `${label}.catch_methods must be an ordered subset of rod, hotspot, harpoon`,
      );
    }
    return methods;
  }

  function validateCatalogCatchMethods(fish) {
    fish.forEach(function (entry, index) {
      validateEntryCatchMethods(entry, `fish[${index}]`);
      catalogKind(entry);
    });
  }

  function normalizeSortField(value) {
    const normalized = String(value || "price").trim().toLowerCase();
    return SORT_FIELD_ORDER.includes(normalized) ? normalized : "price";
  }

  function normalizeSortDirection(value) {
    const normalized = String(value || "desc").trim().toLowerCase();
    return SORT_DIRECTION_ORDER.includes(normalized) ? normalized : "desc";
  }

  function normalizeCatalogView(value) {
    const normalized = String(value || "grade").trim().toLowerCase();
    return FISHYDEX_CATALOG_VIEWS.includes(normalized) ? normalized : "grade";
  }

  function normalizeCatalogState(value) {
    const normalized = String(value || "pending").trim().toLowerCase();
    return CATALOG_STATE_ORDER.includes(normalized) ? normalized : "pending";
  }


  function normalizeSearchQuery(value) {
    if (typeof value !== "string") {
      throw new TypeError("Fishydex search query must be a string");
    }
    const normalized = value.trim();
    if (Array.from(normalized).length > 160 || /[\u0000-\u001f\u007f]/.test(normalized)) {
      throw new TypeError("Fishydex search query exceeds its bounded text contract");
    }
    return normalized;
  }


  function usesAuthoredCatalogPreferences(presentation) {
    return !presentation.search_query && !presentation.show_non_fish
      && !presentation.show_dried && presentation.catalog_view === "grade"
      && presentation.sort_field === "price" && presentation.sort_direction === "desc";
  }

  function storedUiPresentation(presentation) {
    return {
      search_query: normalizeSearchQuery(presentation?.search_query),
      show_non_fish: presentation.show_non_fish,
      sort_field: normalizeSortField(presentation && presentation.sort_field),
      sort_direction: normalizeSortDirection(presentation && presentation.sort_direction),
      catalog_view: normalizeCatalogView(presentation && presentation.catalog_view),
    };
  }

  function repositoryUiPreferences(presentation) {
    const stored = storedUiPresentation(presentation);
    return {
      search_query: stored.search_query,
      show_non_fish: stored.show_non_fish,
      sort_field: stored.sort_field,
      sort_direction: stored.sort_direction,
      catalog_view: stored.catalog_view,
    };
  }

  function pageUiPresentation(preferences) {
    if (!preferences) {
      return null;
    }
    return {
      search_query: preferences.search_query,
      show_non_fish: preferences.show_non_fish,
      sort_field: preferences.sort_field,
      sort_direction: preferences.sort_direction,
      catalog_view: preferences.catalog_view,
    };
  }

  async function persistUiPresentation(presentation, owner) {
    if (!presentation || !owner?.uiRepository) {
      return;
    }
    const preferences = repositoryUiPreferences(presentation);
    const current = owner.uiRepository.state();
    const projection = await owner.uiRepository.replace(preferences, {
      expectedRevision: current?.revision,
    });
    if (!mountIsActive(owner)) {
      return projection;
    }
    owner.uiProjection = projection;
    owner.uiWritable = ["missing", "ready"].includes(projection.kind);
    state.uiProjection = projection;
    state.uiWritable = owner.uiWritable;
    if (projection.kind !== "ready") {
      showToastOnce(
        "fishydex.state.local_storage_unavailable",
        "warning",
        repositoryStatusMessage(projection, "ui")
          || languageText("fishydex.state.local_storage_unavailable"),
      );
      return;
    }
    return projection;
  }

  function loadUiStateFromRepository(projection) {
    const patch = pageUiPresentation(projection.preferences);
    return { patch, statusMessage: repositoryStatusMessage(projection, "ui") };
  }

  function prepareDeviceUiIntent(preferences, owner = state.mountOwner) {
    if (!owner?.uiStateRestored || !owner.uiWritable) {
      return null;
    }
    const normalized = pageUiPresentation(repositoryUiPreferences(preferences));
    const snapshot = { ...clonePresentation(owner.presentation), ...normalized };
    if (snapshot._api_error_message || snapshot._api_error_hint) {
      Object.assign(snapshot, {
        _api_error_message: "",
        _api_error_hint: "",
      });
    }
    if (mountIsActive(owner)) {
      patchPresentation({
        ...normalized,
        _api_error_message: snapshot._api_error_message,
        _api_error_hint: snapshot._api_error_hint,
      }, owner);
    }
    return snapshot;
  }

  async function deviceUiIntentChanged(preferences, owner = state.mountOwner) {
    const snapshot = prepareDeviceUiIntent(preferences, owner);
    return snapshot ? persistUiPresentation(snapshot, owner) : null;
  }

  function queueDomainIntent(operation, owner = state.mountOwner) {
    if (!owner || typeof operation !== "function") {
      return Promise.resolve(null);
    }
    const previous = owner.intentQueue;
    let pending;
    if (previous === null) {
      try {
        pending = Promise.resolve(operation(owner));
      } catch (error) {
        pending = Promise.reject(error);
      }
    } else {
      pending = previous.catch(() => {}).then(() => (
        mountIsActive(owner) ? operation(owner) : null
      ));
    }
    const tracked = pending.finally(() => {
      if (owner.intentQueue === tracked) owner.intentQueue = null;
    });
    owner.intentQueue = tracked;
    return tracked;
  }

  function projectCatalog() {
    const presentation = presentationObject();
    if (presentation) {
      sync(presentation, { catalogUpdated: true });
    }
  }

  function mount(surface, options = {}) {
    state.pageController?.dispose();
    state.lifecycleGeneration += 1;
    const composition = requireStateComposition(options.stateComposition);
    const presentation = createInitialPresentation();
    const initialUi = composition.ui.load();
    const restoredUi = loadUiStateFromRepository(initialUi);
    Object.assign(presentation, restoredUi.patch);
    presentation._query_unavailable = !usesAuthoredCatalogPreferences(presentation);
    const pageRoot = fishydexPageRoot();
    const publishedFishId = selectedFishId({ _selected_fish_id: Number(pageRoot?.dataset.fishydexSelectedItemId) });
    if (pageRoot) delete pageRoot.dataset.fishydexSelectedItemId;
    presentation._selected_fish_id = publishedFishId;
    const owner = {
      publishedFishId,
      generation: state.lifecycleGeneration,
      surface,
      presentation,
      composition,
      currentRepository: composition.current,
      uiRepository: composition.ui,
      currentProjection: null,
      uiProjection: initialUi,
      uiWritable: ["missing", "ready"].includes(initialUi.kind),
      uiStateRestored: false,
      currentUnsubscribe: null,
      uiUnsubscribe: null,
      intentQueue: null,
      cleanups: [],
      stampTimers: new Map(),
      disposed: false,
    };
    state.mountOwner = owner;
    state.surface = surface;
    state.presentation = presentation;
    state.composition = composition;
    state.currentRepository = owner.currentRepository;
    state.uiRepository = owner.uiRepository;
    state.currentProjection = null;
    state.uiProjection = owner.uiProjection;
    state.uiWritable = owner.uiWritable;
    state.uiStateRestored = false;
    connectSurface(surface, presentation);
    state.zoneEvidenceByFish.clear();
    state.zoneEvidenceTabByFish.clear();
    state.fishById = new Map();
    state.renderedCardStateByFish = new Map();
    state.renderedCards.clear();
    state.cardContentKey = "";
    state.guideLookup = null;
    const language = currentLanguage();
    if (!state.historyOwner) {
      state.historyOwner = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
    state.documentTitleSuffix = documentTitleSuffix(document.title);
    state.rootDocumentTitle = String(fishydexPageRoot()?.dataset.fishydexRootTitle || document.title || "");
    if (publishedFishId > 0) {
      window.history.replaceState({
        ...window.history.state,
        [FISHYDEX_HISTORY_STATE_KEY]: { owner: state.historyOwner, fishId: publishedFishId, returnHref: fishydexBaseHref() },
      }, "", window.location.href);
    }
    bindHistoryNavigation();
    patchPresentation({
      lang: language,
      fish: [],
      count: 0,
      revision: "",
      _selected_fish_id: publishedFishId,
    });
    const localizationRoot = fishydexPageRoot() || document.getElementById("fishydex-app");
    if (localizationRoot && languageHelper()) {
      languageHelper().apply(localizationRoot);
    }
    owner.detailNavigation = mountFishDetailNavigation({
      root: document.getElementById("fishydex-details"),
      navigate(entry) {
        const meta = fishMetaById(entry.itemId);
        return meta ? pushFishHistory(meta, entry.href, "replace") : false;
      },
    });
    owner.cleanups.push(() => owner.detailNavigation?.dispose());
    owner.query = mountFishydexQuery({ root: surface, t: languageText, changed: () => {
      if (mountIsActive(owner)) sync(owner.presentation);
    } });
    owner.cleanups.push(() => owner.query.dispose());
    owner.titleControls = mountTitleControls({
      root: fishydexPageRoot(),
      repository: owner.currentRepository,
      resolveApiUrl: apiUrl,
      t: languageText,
      guideCount: guideCountForEntry,
      encyclopediaKey: fishGuideOrderForEntry,
      onCatalog: (catalog, kind) => owner.query.setTitles(catalog, kind),
    });
    listen(window, "fishystuff:languagechange", () => {
      if (mountIsActive(owner)) sync(owner.presentation);
    }, undefined, owner);
    const contestElement = surface.querySelector("fishy-fishing-contest");
    if (contestElement) {
      listen(contestElement, "click", (event) => {
        if (!(event.target instanceof Element) || event.target.closest("[data-contest-add]")) return;
        const button = event.target.closest("[data-contest-slot]")?.querySelector("[data-contest-fish-details]");
        if (!(button instanceof HTMLButtonElement)) return;
        const itemId = Number(button.dataset.contestFishDetails);
        if (!contestElement.selectionProjection.itemIds.includes(itemId)) return;
        const meta = fishMetaById(itemId);
        if (meta) pushFishHistory(meta, fishDetailHref(meta));
      }, undefined, owner);
      sync(owner.presentation);
    }
    owner.uiStateRestored = true;
    state.uiStateRestored = true;
    owner.uiUnsubscribe = owner.uiRepository.subscribe((projection) => {
      if (!mountIsActive(owner) || !owner.uiStateRestored) {
        return;
      }
      owner.uiProjection = projection;
      owner.uiWritable = ["missing", "ready"].includes(projection.kind);
      state.uiProjection = projection;
      state.uiWritable = owner.uiWritable;
      const preferences = pageUiPresentation(projection.preferences);
      if (!presentationObject(owner) || !preferences) {
        const statusMessage = repositoryStatusMessage(projection, "ui");
        sync(owner.presentation);
        if (statusMessage) {
          showToastOnce("fishydex.ui-repository.subscription", "warning", statusMessage);
        }
        return;
      }
      patchPresentation(preferences, owner);
      const statusMessage = repositoryStatusMessage(projection, "ui");
      if (statusMessage) {
        showToastOnce("fishydex.ui-repository.subscription", "warning", statusMessage);
      }
    });
    state.uiUnsubscribe = owner.uiUnsubscribe;
    owner.currentUnsubscribe = owner.currentRepository.subscribe((projection) => {
      if (!mountIsActive(owner)) {
        return;
      }
      const previous = owner.currentProjection;
      const changed = fishProgressChanges(previous, projection);
      owner.currentProjection = projection;
      state.currentProjection = projection;
      syncCurrentRepositoryStatus(projection);
      if (
        projection.catchCounts !== null
        && projection.favouriteIds !== null
        && projection.showDried !== null
        && projection.fishGuideProgress !== null
      ) {
        if (changed !== null && owner.uiStateRestored) {
          syncChangedProgress(owner, previous, projection, changed);
        } else {
          if (fishGuideProjectionKey(previous) !== fishGuideProjectionKey(projection)) {
            state.fishGuideProgress = projection.fishGuideProgress;
          }
          patchPresentation({ show_dried: projection.showDried }, owner);
        }
      } else if (owner.uiStateRestored) {
        sync(owner.presentation);
      }
      const statusMessage = repositoryStatusMessage(projection);
      if (statusMessage) {
        showToastOnce("fishydex.current-repository.subscription", "warning", statusMessage);
      }
    });
    state.currentUnsubscribe = owner.currentUnsubscribe;
    listen(owner.surface, "fishydex-intent", (event) => {
      const detail = event?.detail;
      if (!detail || typeof detail !== "object" || Array.isArray(detail)) {
        throw new TypeError("Fishydex intent must carry a typed command");
      }
      if (detail.kind === "replace-device-ui") {
        requireExactFields(detail, ["kind", "preferences"], "Fishydex device UI intent");
        const preferences = clonePresentation(requireExactFields(
          detail.preferences,
          DEVICE_UI_INTENT_FIELDS,
          "Fishydex device UI preferences",
        ));
        const snapshot = prepareDeviceUiIntent(preferences, owner);
        void queueDomainIntent(
          (capturedOwner) => snapshot
            ? persistUiPresentation(snapshot, capturedOwner)
            : null,
          owner,
        );
        return;
      }
      if (detail.kind === "set-show-dried" && typeof detail.enabled === "boolean") {
        requireExactFields(detail, ["kind", "enabled"], "Fishydex show-dried intent");
        const enabled = detail.enabled;
        void queueDomainIntent(
          (capturedOwner) => applyShowDriedIntent(enabled, capturedOwner),
          owner,
        );
        return;
      }
      if (detail.kind === "clear-filters") {
        requireExactFields(
          detail,
          ["kind", "preferences", "showDried"],
          "Fishydex clear-filters intent",
        );
        const preferences = clonePresentation(requireExactFields(
          detail.preferences,
          DEVICE_UI_INTENT_FIELDS,
          "Fishydex clear-filters device UI preferences",
        ));
        if (preferences.show_non_fish !== false) {
          throw new TypeError("Fishydex clear-filters intent must disable non-fish items");
        }
        if (detail.showDried !== false) {
          throw new TypeError("Fishydex clear-filters intent must disable dried items");
        }
        const snapshot = prepareDeviceUiIntent(preferences, owner);
        if (!snapshot) patchPresentation({}, owner);
        void queueDomainIntent(async (capturedOwner) => {
          if (snapshot) {
            await persistUiPresentation(snapshot, capturedOwner);
          }
          await applyShowDriedIntent(false, capturedOwner);
        }, owner);
        return;
      }
      throw new TypeError("Fishydex intent has an unsupported command");
    }, undefined, owner);
    const ready = owner.composition.load().then((projection) => {
      if (!mountIsActive(owner)) {
        return projection.current;
      }
      owner.currentProjection = projection.current;
      sync(presentation);
      if (restoredUi.statusMessage) {
        showToastOnce("fishydex.restore.status", "warning", restoredUi.statusMessage);
      }
      return projection.current;
    }).catch((error) => {
      if (typeof options.onError === "function") {
        options.onError(error);
      } else {
        console.error("Fishydex Current repository load failed", error);
      }
      throw error;
    });
    const controller = Object.freeze({
      ready,
      state() {
        return owner.currentProjection ?? owner.currentRepository.state();
      },
      presentation() {
        return owner.disposed ? null : clonePresentation(owner.presentation);
      },
      async flush() {
        await ready;
        while (owner.intentQueue) {
          await owner.intentQueue;
        }
        return owner.currentRepository.state();
      },
      dispose() {
        if (owner.disposed) {
          return;
        }
        owner.disposed = true;
        const wasActive = state.mountOwner === owner;
        if (wasActive) {
          cancelPageRequests(owner.generation);
        }
        if (wasActive && state.lifecycleGeneration === owner.generation) {
          state.lifecycleGeneration += 1;
        }
        owner.currentUnsubscribe?.();
        owner.currentUnsubscribe = null;
        owner.uiUnsubscribe?.();
        owner.uiUnsubscribe = null;
        owner.titleControls?.dispose();
        for (const cleanup of owner.cleanups.splice(0)) {
          cleanup();
        }
        for (const timer of owner.stampTimers.values()) {
          window.clearTimeout(timer);
        }
        owner.stampTimers.clear();
        if (wasActive) {
          cancelAuthoredCatalogHydration();
          state.gridBound = false;
          state.detailsBound = false;
          state.historyBound = false;
          state.currentUnsubscribe = null;
          state.uiUnsubscribe = null;
          state.currentProjection = null;
          state.presentation = null;
          state.surface = null;
          state.mountOwner = null;
          state.pageController = null;
        }
      },
    });
    state.pageController = controller;
    return controller;
  }

  function queueStamp(key, fishId, owner = state.mountOwner) {
    const presentation = presentationObject(owner);
    if (!presentation || !key || typeof key !== "string") {
      return {};
    }
    const currentTimer = owner.stampTimers.get(key);
    if (currentTimer) {
      window.clearTimeout(currentTimer);
      owner.stampTimers.delete(key);
    }
    const stamp = Number.isSafeInteger(fishId) ? fishId : null;
    if (!Number.isSafeInteger(fishId)) {
      return { [key]: stamp };
    }
    const timer = window.setTimeout(function () {
      const activePresentation = presentationObject(owner);
      if (activePresentation && activePresentation[key] === fishId) {
        applyStampPatch({ [key]: null }, owner);
      }
      owner.stampTimers.delete(key);
    }, 420);
    owner.stampTimers.set(key, timer);
    return { [key]: stamp };
  }

  function applyStampPatch(patch, owner) {
    if (!mountIsActive(owner)) return;
    const snapshot = owner.presentation;
    const changed = new Set(Object.keys(patch).flatMap((key) => [snapshot[key], patch[key]])
      .filter(Number.isSafeInteger));
    const caught = new Set();
    for (const id of changed) {
      const entry = state.fishById.get(id);
      if (!entry) continue;
      const count = fishGuideCatchCountForEntry(entry);
      if (count.kind === "known" && count.count > 0) caught.add(id);
    }
    const favourite = new Set(owner.currentProjection?.favouriteIds || []);
    const wasSyncing = state.syncing;
    state.syncing = true;
    try {
      patchPresentation(patch, owner);
      syncRenderedCardState(snapshot, caught, favourite, changed);
      const selectedId = selectedFishId(snapshot);
      if (changed.has(selectedId)) {
        renderDetailsCount(snapshot);
        const entry = state.fishById.get(selectedId);
        if (entry) applyFavouriteButtonState(
          document.getElementById("fishydex-details-favourite-toggle"),
          fishDisplayName(entry, selectedId), favourite.has(selectedId),
          currentStamp(snapshot, "_favourite_stamp_fish_id", selectedId),
        );
      }
    } finally {
      state.syncing = wasSyncing;
    }
  }

  function fishItemId(entry) {
    const value = Number(entry && (entry.item_id ?? entry.itemId));
    return Number.isSafeInteger(value) && value > 0 && value <= 4_294_967_295 ? value : 0;
  }

  function integerValue(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : null;
  }

  function fishIconItemId(entry) {
    const value = integerValue(entry && (entry.icon_item_id ?? entry.iconItemId));
    return value != null && value > 0 ? value : null;
  }

  function fishEncyclopediaId(entry) {
    const value = Number(entry && entry.encyclopedia_id);
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  function fishEncyclopediaIconImage(entry) {
    return normalizeItemIconPath(
      entry && (entry.encyclopedia_icon_image ?? entry.encyclopediaIconImage),
    );
  }

  function fishGuideOrderForEntry(entry) {
    const value = Number(entry && entry.encyclopedia_key);
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  function fishGuideCategoryCharForKey(key) {
    const index = Number(key) - 1;
    if (!Number.isInteger(index) || index < 0 || index >= FISH_GUIDE_CATEGORY_SEQUENCE.length) {
      return "?";
    }
    return FISH_GUIDE_CATEGORY_SEQUENCE.charAt(index) || "?";
  }

  function fishGuideCategoryForEntry(entry) {
    switch (fishGuideCategoryCharForKey(fishGuideOrderForEntry(entry))) {
      case "h":
        return "other";
      case "f":
        return "freshwater";
      case "s":
        return "saltwater";
      case "c":
        return "crustacean";
      default:
        return "unknown";
    }
  }

  function fishGuideCategoryLabel(category) {
    const translated = languageText(`fishydex.group.guide.${category}`);
    return translated === `fishydex.group.guide.${category}`
      ? languageText("fishydex.group.guide.unknown")
      : translated;
  }

  function filterGradeForEntry(entry) {
    const tones = window.__fishystuffGeneratedI18n?.presentation?.itemGradeTones;
    const tone = tones && entry ? tones[entry.grade] : "";
    return GRADE_COLOR_ORDER.includes(tone) ? tone : "unknown";
  }

  function gradeLabelForKey(value) {
    const translated = languageText(`domain.grade.${value}`);
    return translated === `domain.grade.${value}`
      ? languageText("domain.grade.unknown")
      : translated;
  }

  function completionPercent(caughtCount, totalCount) {
    const caught = Number(caughtCount);
    const total = Number(totalCount);
    if (
      !Number.isSafeInteger(caught)
      || !Number.isSafeInteger(total)
      || caught < 0
      || total <= 0
      || caught > total
    ) {
      return 0;
    }
    return Math.max(0, Math.min(100, Math.round((caught * 100) / total)));
  }

  function entryCatchMethods(entry) {
    return validateEntryCatchMethods(entry, "fish entry");
  }

  function entryIsDried(entry) {
    return normalizeBooleanFlag(entry && entry.is_dried);
  }

  function compareFishNames(left, right) {
    return String((left && left.name) || "").localeCompare(String((right && right.name) || ""), currentLanguage().locale, {
      sensitivity: "base",
      numeric: true,
    });
  }

  function entryVendorPrice(entry) {
    const amount = Number(entry && entry.vendor_price);
    return Number.isFinite(amount) && amount > 0 ? amount : null;
  }

  function compareFishEntries(left, right, sortField, sortDirection) {
    if (sortField === "price") {
      const leftPrice = entryVendorPrice(left);
      const rightPrice = entryVendorPrice(right);
      if (leftPrice === null && rightPrice !== null) {
        return 1;
      }
      if (leftPrice !== null && rightPrice === null) {
        return -1;
      }
      if (leftPrice !== null && rightPrice !== null && leftPrice !== rightPrice) {
        return sortDirection === "desc" ? rightPrice - leftPrice : leftPrice - rightPrice;
      }
    }
    const nameOrder = compareFishNames(left, right);
    if (nameOrder !== 0) {
      return sortDirection === "desc" && sortField === "name" ? -nameOrder : nameOrder;
    }
    return fishItemId(left) - fishItemId(right);
  }

  function compareFishGuideEntries(left, right) {
    const leftCategoryIndex = FISH_GUIDE_CATEGORY_ORDER.indexOf(fishGuideCategoryForEntry(left));
    const rightCategoryIndex = FISH_GUIDE_CATEGORY_ORDER.indexOf(fishGuideCategoryForEntry(right));
    if (leftCategoryIndex !== rightCategoryIndex) {
      return leftCategoryIndex - rightCategoryIndex;
    }

    const leftGuideOrder = fishGuideOrderForEntry(left);
    const rightGuideOrder = fishGuideOrderForEntry(right);
    if (leftGuideOrder === null && rightGuideOrder !== null) {
      return 1;
    }
    if (leftGuideOrder !== null && rightGuideOrder === null) {
      return -1;
    }
    if (leftGuideOrder !== null && rightGuideOrder !== null && leftGuideOrder !== rightGuideOrder) {
      return leftGuideOrder - rightGuideOrder;
    }

    const nameOrder = compareFishNames(left, right);
    if (nameOrder !== 0) {
      return nameOrder;
    }
    return fishItemId(left) - fishItemId(right);
  }

  function fishRenderSignature(fish) {
    let hash = 2166136261;
    for (const entry of fish) {
      const raw = entry
        ? `${fishItemId(entry)}|${fishEncyclopediaId(entry) || 0}|${fishEncyclopediaIconImage(entry)}|${fishGuideOrderForEntry(entry) || 0}|${entry.grade || ""}|${entry.is_prize === true ? 1 : 0}|${entry.name || ""}|${entryIsDried(entry) ? 1 : 0}|${catalogKind(entry)}|${entryCatchMethods(entry).join(",")}|${entry.vendor_price || 0}`
        : "null";
      for (let index = 0; index < raw.length; index += 1) {
        hash ^= raw.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
    }
    return `${fish.length}:${(hash >>> 0).toString(16)}`;
  }

  function fishGuideProgressSignature(progress) {
    if (progress?.status !== "available" || !progress.snapshot) {
      return String(progress?.status || "unknown");
    }
    return [
      progress.snapshot.subject.serviceRegion,
      progress.snapshot.subject.familyId,
      progress.snapshot.observedAt,
      progress.snapshot.client.buildId,
      progress.snapshot.client.sha256,
      progress.snapshot.coverage,
      progress.snapshot.entries.map(function (entry) {
        return `${entry.encyclopediaKey}:${entry.catchCount}`;
      }).join(","),
    ].join("|");
  }

  function createElement(tagName, className, textContent) {
    const element = document.createElement(tagName);
    if (className) {
      element.className = className;
    }
    if (textContent !== undefined) {
      element.textContent = textContent;
    }
    return element;
  }

  function formatSilver(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount <= 0) {
      return languageText("fishydex.state.unavailable");
    }
    return SILVER_FORMATTER.format(amount);
  }

  function formatCatchCount(value) {
    const count = Number(value);
    return Number.isInteger(count) && count >= 0
      ? CATCH_COUNT_FORMATTER.format(count)
      : languageText("fishydex.details.unknown");
  }

  function catchCountText(countState) {
    switch (countState && countState.kind) {
      case "known":
        return languageText("fishydex.guide.catch_count", {
          count: formatCatchCount(countState.count),
        });
      case "unavailable":
        return languageText("fishydex.state.unavailable");
      case "not_applicable":
        return languageText("fishydex.state.not_applicable");
      default:
        return languageText("fishydex.details.unknown");
    }
  }

  function populateVendorPrice(element, value) {
    if (!(element instanceof HTMLElement)) {
      return;
    }
    const icon = createElement("span", "fishydex-price-icon");
    icon.innerHTML = ICON_COIN_STACK;
    const amount = createElement("span", "fishydex-price-value", formatSilver(value));
    element.replaceChildren(icon, amount);
  }

  function createVendorPriceElement(tagName, className, value) {
    const element = createElement(tagName, className);
    populateVendorPrice(element, value);
    return element;
  }

  function fishItemIconPath(itemId) {
    if (!Number.isInteger(itemId) || itemId <= 0) {
      return "";
    }
    return `/images/items/${String(itemId).padStart(8, "0")}.webp`;
  }

  function canonicalIconStem(stem) {
    const normalized = String(stem || "").trim();
    const iconMatch = normalized.match(/^ic_(\d+)$/i);
    return iconMatch ? `IC_${iconMatch[1]}` : normalized;
  }

  function normalizeItemIconPath(value) {
    const raw = String(value || "").trim();
    if (!raw) {
      return "";
    }
    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }

    const withoutQuery = raw.split(/[?#]/, 1)[0].replaceAll("\\", "/").trim();
    if (!withoutQuery) {
      return "";
    }
    const basename = withoutQuery.split("/").pop() || "";
    const extensionMatch = basename.match(/\.([a-z0-9]+)$/i);
    const stem = canonicalIconStem(
      extensionMatch ? basename.slice(0, -extensionMatch[0].length) : basename,
    );
    if (!stem) {
      return "";
    }

    const hasIconPathPrefix = /^\/?images\/items\//i.test(withoutQuery);
    const hasKnownIconExtension =
      extensionMatch && /^(?:dds|png|webp|jpg|jpeg|gif|avif)$/i.test(extensionMatch[1]);
    const hasSourceIconPath =
      /(?:^|\/)(?:ui_texture|new_icon|quest|fishicons|icon)(?:\/|$)/i.test(withoutQuery);
    const looksLikeKnownIconStem =
      /^(?:IC_\d+|\d{5,8}(?:_\d+)?|[A-Za-z][A-Za-z0-9_]*\d[A-Za-z0-9_]*)$/.test(stem);
    if (!hasIconPathPrefix && !hasKnownIconExtension && !hasSourceIconPath && !looksLikeKnownIconStem) {
      return "";
    }
    return `/images/items/${stem}.webp`;
  }

  function normalizeBaseUrl(value) {
    return String(value || "").trim().replace(/\/+$/, "");
  }

  function isLoopbackHost(hostname) {
    return hostname === "127.0.0.1" || hostname === "localhost";
  }

  function deriveSiblingBaseUrl(baseUrl, subdomain) {
    const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
    const normalizedSubdomain = String(subdomain || "").trim().replace(/\.+$/, "");
    if (!normalizedBaseUrl || !normalizedSubdomain) {
      return "";
    }
    try {
      const url = new URL(normalizedBaseUrl);
      if (!url.hostname || isLoopbackHost(url.hostname)) {
        return "";
      }
      url.hostname = `${normalizedSubdomain}.${url.hostname}`;
      url.pathname = "";
      url.search = "";
      url.hash = "";
      return normalizeBaseUrl(url.toString());
    } catch (_error) {
      return "";
    }
  }

  function fallbackPublicBaseUrl(subdomain, productionBase, localPort) {
    const location = window.location || null;
    if (location && isLoopbackHost(location.hostname) && localPort) {
      const protocol = location.protocol === "https:" ? "https:" : "http:";
      return `${protocol}//${location.hostname}:${localPort}`;
    }
    return deriveSiblingBaseUrl(location && location.origin, subdomain) || productionBase;
  }

  function cdnUrl(path) {
    if (typeof window.__fishystuffResolveCdnUrl === "function") {
      return window.__fishystuffResolveCdnUrl(path);
    }
    const runtimeConfig = window.__fishystuffRuntimeConfig || {};
    const base = normalizeBaseUrl(
      window.__fishystuffCdnBaseUrl
      || runtimeConfig.cdnBaseUrl
      || fallbackPublicBaseUrl("cdn", "https://cdn.fishystuff.fish", "4040"),
    );
    const raw = String(path || "").trim();
    if (!raw) {
      return "";
    }
    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }
    if (raw.startsWith("/")) {
      return `${base}${raw}`;
    }
    return `${base}/${raw.replace(/^\/+/, "")}`;
  }

  function fishItemIconUrl(fishOrItemId) {
    if (fishOrItemId?.catalog_kind === "item" && fishOrItemId.icon_image === null) return "";
    const entry = fishOrItemId && typeof fishOrItemId === "object" ? fishOrItemId : null;
    const directIconPath = entry
      ? normalizeItemIconPath(entry.icon_image ?? entry.iconImage)
      : "";
    if (directIconPath) {
      return cdnUrl(directIconPath);
    }

    const itemId = entry ? fishItemId(entry) : integerValue(fishOrItemId);
    const iconItemId = entry ? fishIconItemId(entry) || itemId : itemId;
    if (!Number.isInteger(iconItemId) || iconItemId <= 0) {
      return "";
    }
    if (typeof window.__fishystuffResolveFishItemIconUrl === "function") {
      return window.__fishystuffResolveFishItemIconUrl(iconItemId);
    }
    const path = fishItemIconPath(iconItemId);
    return path ? cdnUrl(path) : "";
  }

  function fishEncyclopediaIconUrl(iconImage) {
    const path = normalizeItemIconPath(iconImage);
    return path ? cdnUrl(path) : "";
  }

  function setImageWithPlaceholder(image, placeholder, src, alt) {
    if (!(image instanceof HTMLImageElement) || !(placeholder instanceof HTMLElement)) {
      return;
    }
    image.onload = null;
    image.onerror = null;
    image.dataset.expectedSrc = src || "";
    if (!src) {
      image.hidden = true;
      image.removeAttribute("src");
      image.alt = "";
      placeholder.hidden = false;
      return;
    }
    placeholder.hidden = true;
    image.hidden = false;
    image.alt = alt;
    image.onload = function () {
      if (image.dataset.expectedSrc === src) {
        placeholder.hidden = true;
        image.hidden = false;
      }
    };
    image.onerror = function () {
      if (image.dataset.expectedSrc !== src) {
        return;
      }
      image.onload = null;
      image.onerror = null;
      image.hidden = true;
      image.removeAttribute("src");
      image.alt = "";
      placeholder.hidden = false;
    };
    image.src = src;
  }

  function setElementText(element, text) {
    if (element && element.textContent !== text) {
      element.textContent = text;
    }
  }

  function setElementLink(element, text, href) {
    if (!element) {
      return;
    }
    if (!href) {
      element.textContent = text;
      return;
    }
    const link = document.createElement("a");
    link.className = "fishydex-details-link link link-hover";
    link.href = href;
    link.target = "_blank";
    link.rel = "noreferrer noopener";
    link.textContent = text;
    element.replaceChildren(link);
  }

  function bdolyticsItemUrl(itemKey) {
    if (!Number.isInteger(itemKey)) {
      return "";
    }
    return `https://bdolytics.com/en/NA/db/item/${itemKey}`;
  }

  function currentStamp(snapshot, key, fishId) {
    return Number.isInteger(fishId) && snapshot[key] === fishId;
  }

  function renderedCardStateKey(
    fishId,
    isCaught,
    isFavourite,
    guideCount,
    snapshot,
  ) {
    return [
      isCaught ? "caught" : "missing",
      isFavourite ? "favourite" : "ordinary",
      currentStamp(snapshot, "_caught_stamp_fish_id", fishId) ? "caught-stamp" : "",
      currentStamp(snapshot, "_favourite_stamp_fish_id", fishId) ? "favourite-stamp" : "",
      guideCount?.kind || "unknown",
      guideCount?.kind === "known" ? guideCount.count : "",
    ].join("|");
  }

  function renderEmptyState(hasActiveFilters) {
    const empty = createElement("div", "fishydex-empty card card-dash bg-base-100");
    const body = createElement("div", "card-body items-center");
    const title = createElement("h3", "fishydex-empty-title", languageText("fishydex.empty.title"));
    const detail = createElement(
      "p",
      "fishydex-subtle",
      hasActiveFilters
        ? languageText("fishydex.empty.filtered")
        : languageText("fishydex.empty.unfiltered")
    );
    body.append(title, detail);
    empty.appendChild(body);
    return empty;
  }

  const FAVOURITE_BUTTON_CLASSES = Object.freeze([
    "fishydex-favourite-button btn btn-lg sm:btn-md btn-circle shadow-none btn-ghost",
    "fishydex-favourite-button btn btn-lg sm:btn-md btn-circle shadow-none btn-ghost is-stamping",
    "fishydex-favourite-button btn btn-lg sm:btn-md btn-circle shadow-none btn-soft btn-error is-favourite",
    "fishydex-favourite-button btn btn-lg sm:btn-md btn-circle shadow-none btn-soft btn-error is-favourite is-stamping",
  ]);

  const CAUGHT_BUTTON_CLASSES = Object.freeze([
    "fishydex-caught-button btn btn-lg sm:btn-md btn-circle shadow-none btn-ghost",
    "fishydex-caught-button btn btn-lg sm:btn-md btn-circle shadow-none btn-ghost is-stamping",
    "fishydex-caught-button btn btn-lg sm:btn-md btn-circle shadow-none btn-soft btn-success is-caught",
    "fishydex-caught-button btn btn-lg sm:btn-md btn-circle shadow-none btn-soft btn-success is-caught is-stamping",
  ]);

  function buttonStateClass(classes, isActive, isStamping) {
    return classes[(Number(isActive) * 2) + Number(isStamping)];
  }

  function applyFavouriteButtonState(button, fishName, isFavourite, isStamping) {
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }
    if (button.className === buttonStateClass(FAVOURITE_BUTTON_CLASSES, isFavourite, isStamping)
      && button.getAttribute("aria-label") === favouriteToggleLabel(fishName, isFavourite)) return;
    button.className = buttonStateClass(FAVOURITE_BUTTON_CLASSES, isFavourite, isStamping);
    button.setAttribute("aria-pressed", isFavourite ? "true" : "false");
    const label = favouriteToggleLabel(fishName, isFavourite);
    button.setAttribute("aria-label", label);
    button.setAttribute("data-fishy-tooltip", label);
    button.setAttribute("data-fishy-tooltip-mode", "anchor-only");
    button.innerHTML = isFavourite ? ICON_HEART_FILL : ICON_HEART_LINE;
  }

  function applyCaughtButtonState(button, fishName, isCaught, isStamping) {
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }
    if (button.className === buttonStateClass(CAUGHT_BUTTON_CLASSES, isCaught, isStamping)
      && button.getAttribute("aria-label") === caughtToggleLabel(fishName, isCaught)
      && !button.disabled) return;
    button.className = buttonStateClass(CAUGHT_BUTTON_CLASSES, isCaught, isStamping);
    button.setAttribute("aria-pressed", isCaught ? "true" : "false");
    button.disabled = false;
    const label = caughtToggleLabel(fishName, isCaught);
    button.setAttribute("aria-label", label);
    button.setAttribute("data-fishy-tooltip", label);
    button.setAttribute("data-fishy-tooltip-mode", "anchor-only");
    button.innerHTML = isCaught ? ICON_CAUGHT_FILL : ICON_CAUGHT_LINE;
  }

  function renderFishCard(fish, caughtSet, favouriteSet, snapshot, animationIndex, animateCards) {
    const itemId = fishItemId(fish);
    const fishName = fishDisplayName(fish, itemId);
    const gradeTone = filterGradeForEntry(fish);
    const isCaught = caughtSet.has(itemId);
    const isFavourite = favouriteSet.has(itemId);
    const guideCount = fishGuideCatchCountForEntry(fish);
    state.renderedCardStateByFish.set(
      itemId,
      renderedCardStateKey(
        itemId,
        isCaught,
        isFavourite,
        guideCount,
        snapshot,
      ),
    );
    const card = createElement("article", "fishydex-card card card-border bg-base-100");
    card.dataset.fishId = String(itemId);
    if (animateCards) {
      card.classList.add("is-entering");
      card.style.setProperty("--fishydex-card-delay", `${Math.min(animationIndex, 11) * 24}ms`);
    }
    if (isCaught) {
      card.classList.add("is-caught");
    }

    const openLink = createElement("a", "fishydex-card-open");
    const detailHref = fishDetailHref(fish);
    if (detailHref) {
      openLink.href = detailHref;
    } else {
      openLink.setAttribute("aria-disabled", "true");
    }
    openLink.dataset.action = "open-details";
    openLink.setAttribute("aria-haspopup", "dialog");
    openLink.setAttribute("aria-label", languageText("fishydex.details.open", { name: fishName }));

    const content = createElement("div", "fishydex-card-content card-body");
    const top = createElement("div", "fishydex-card-top");
    const actions = createElement("div", "fishydex-card-actions");

    const favouriteButton = createElement(
      "button"
    );
    favouriteButton.type = "button";
    favouriteButton.dataset.action = "toggle-favourite";
    applyFavouriteButtonState(
      favouriteButton,
      fishName,
      isFavourite,
      currentStamp(snapshot, "_favourite_stamp_fish_id", itemId)
    );

    const caughtButton = createElement(
      "button"
    );
    caughtButton.type = "button";
    caughtButton.dataset.action = "toggle-caught";
    applyCaughtButtonState(
      caughtButton,
      fishName,
      isCaught,
      currentStamp(snapshot, "_caught_stamp_fish_id", itemId),
    );

    const caughtControl = createElement("div", "fishydex-caught-control");
    caughtControl.append(caughtButton);
    if (guideCount.kind === "known") {
      const catchCount = createElement(
        "span",
        "fishydex-caught-count",
        languageText("fishydex.guide.catch_count_short", {
          count: formatCatchCount(guideCount.count),
        }),
      );
      catchCount.tabIndex = 0;
      catchCount.setAttribute("aria-label", catchCountText(guideCount));
      catchCount.dataset.fishyTooltip = catchCountText(guideCount);
      caughtControl.append(catchCount);
    }
    actions.append(caughtControl);
    top.append(favouriteButton, actions);

    const main = createElement("div", "fishydex-card-main");
    const iconWrap = createElement(
      "div",
      `fishydex-icon-wrap fishy-item-icon-frame is-lg fishy-item-grade-${gradeTone}`
    );
    const icon = createElement("img", "fishydex-icon fishy-item-icon");
    icon.loading = "lazy";
    const placeholder = createElement(
      "div",
      `fishydex-placeholder fishy-item-icon-fallback fishy-item-grade-${gradeTone}`,
      "?"
    );
    setImageWithPlaceholder(
      icon,
      placeholder,
      fishItemIconUrl(fish),
      languageText("fishydex.details.icon_alt", { name: fishName }),
    );
    iconWrap.append(icon, placeholder);

    main.appendChild(iconWrap);
    const name = createElement(
      "div",
      `fishydex-name fishy-domain-label fishy-item-label fishy-item-grade-${gradeTone}`,
      fishName,
    );
    applyDataLanguage(name);
    main.appendChild(name);
    if (catalogKind(fish) === "item") {
      main.appendChild(createElement("span", "badge badge-ghost badge-sm", languageText("fishydex.catalog.kind.item")));
    }
    main.appendChild(createVendorPriceElement("div", "fishydex-price fishydex-card-price", entryVendorPrice(fish)));

    content.append(top, main);
    card.append(openLink, content);
    state.mountOwner?.titleControls?.mountCard(card, fish);
    state.renderedCards.set(itemId, card);
    return card;
  }

  function cancelAuthoredCatalogHydration() {
    state.authoredHydrationRevision += 1;
  }

  function hydrateAuthoredCatalog(grid, fish, caughtIds, favouriteIds, snapshot) {
    const authoredCards = Array.from(grid.querySelectorAll("[data-fish-id]"));
    if (authoredCards.length === 0) {
      return false;
    }
    const hydrationRevision = state.authoredHydrationRevision + 1;
    state.authoredHydrationRevision = hydrationRevision;
    grid.dataset.fishydexAuthoredCatalog = "hydrating";
    grid.dataset.fishydexCatalogState = "ready";
    const caughtSet = new Set(caughtIds);
    const favouriteSet = new Set(favouriteIds);
    let index = 0;

    const hydrateBatch = function () {
      if (state.authoredHydrationRevision !== hydrationRevision) {
        return;
      }
      const end = Math.min(index + 12, authoredCards.length);
      while (index < end) {
        const authoredCard = authoredCards[index];
        const fishId = Number.parseInt(authoredCard.dataset.fishId || "", 10);
        const entry = state.fishById.get(fishId);
        if (entry && authoredCard.isConnected) {
          authoredCard.replaceWith(
            renderFishCard(entry, caughtSet, favouriteSet, snapshot, index, false),
          );
        }
        index += 1;
      }
      if (index < authoredCards.length) {
        window.requestAnimationFrame(hydrateBatch);
      } else if (state.authoredHydrationRevision === hydrationRevision) {
        grid.dataset.fishydexAuthoredCatalog = "hydrated";
      }
    };

    window.requestAnimationFrame(hydrateBatch);
    return true;
  }

  async function toggleCaught(fishId, owner, fish) {
    if (
      !owner?.currentRepository
      || !Number.isSafeInteger(fishId)
      || fishId <= 0
      || fishId > 4_294_967_295
    ) {
      return null;
    }
    const repositoryState = owner.currentRepository.state();
    const count = fishGuideCatchCountForEntry(fish || { itemId: fishId }, repositoryState);
    const currentSharedFish = sharedFishSnapshot(owner.currentRepository);
    if (!currentSharedFish) {
      return null;
    }
    const isCaught = !(count.kind === "known" && count.count > 0);
    const key = fishGuideOrderForEntry(fish);
    const projection = await owner.currentRepository.setCaught(fishId, isCaught, {
      ...(Number.isInteger(key) && key > 0 ? { encyclopediaKey: key } : {}),
    });
    if (projection.kind !== "ready") {
      if (mountIsActive(owner)) {
        showToastOnce(
          `fishydex.repository.caught.${projection.kind}`,
          "warning",
          repositoryStatusMessage(projection)
            || languageText("fishydex.state.local_storage_unavailable"),
        );
      }
      return projection;
    }
    if (mountIsActive(owner)) {
      state.suppressCardAnimation = true;
      const stampPatch = queueStamp(
        "_caught_stamp_fish_id",
        isCaught ? fishId : null,
        owner,
      );
      applyStampPatch(stampPatch, owner);
    }
    return projection;
  }

  function queueCaughtIntent(fishId, owner = state.mountOwner) {
    if (mountIsActive(owner)) owner.titleControls?.discardCountDraft(fishId);
    const fish = mountIsActive(owner) ? state.fishById.get(fishId) : null;
    return queueDomainIntent(
      (capturedOwner) => toggleCaught(fishId, capturedOwner, fish),
      owner,
    );
  }

  function syncRenderedCardState(snapshot, caughtSet, favouriteSet, changed = null) {
    const cards = changed === null
      ? document.querySelectorAll("#fishydex-grid [data-fish-id]")
      : Array.from(changed, (id) => state.renderedCards.get(id)).filter((card) => card?.isConnected);
    for (const card of cards) {
      if (!(card instanceof HTMLElement)) {
        continue;
      }
      const fishId = Number.parseInt(card.dataset.fishId || "", 10);
      if (!Number.isInteger(fishId)) {
        continue;
      }
      const fish = state.fishById.get(fishId);
      if (!fish) {
        continue;
      }
      const fishName = fishDisplayName(fish, fishId);
      const isCaught = caughtSet.has(fishId);
      const isFavourite = favouriteSet.has(fishId);
      const guideCount = fishGuideCatchCountForEntry(fish);
      const cardStateKey = renderedCardStateKey(fishId, isCaught, isFavourite, guideCount, snapshot);
      if (state.renderedCardStateByFish.get(fishId) === cardStateKey) continue;
      const countLabel = card.querySelector(".fishydex-caught-count");
      if (countLabel) {
        countLabel.hidden = guideCount.kind !== "known";
        countLabel.textContent = guideCount.kind === "known"
          ? languageText("fishydex.guide.catch_count_short", { count: formatCatchCount(guideCount.count) })
          : "";
        countLabel.setAttribute("aria-label", catchCountText(guideCount));
        countLabel.dataset.fishyTooltip = catchCountText(guideCount);
      } else if (guideCount.kind === "known") {
        const label = createElement("span", "fishydex-caught-count",
          languageText("fishydex.guide.catch_count_short", { count: formatCatchCount(guideCount.count) }));
        label.tabIndex = 0;
        label.setAttribute("aria-label", catchCountText(guideCount));
        label.dataset.fishyTooltip = catchCountText(guideCount);
        card.querySelector(".fishydex-caught-control")?.append(label);
      }
      card.classList.toggle("is-caught", isCaught);

      const favouriteButton = card.querySelector("[data-action='toggle-favourite']");
      applyFavouriteButtonState(
        favouriteButton,
        fishName,
        isFavourite,
        currentStamp(snapshot, "_favourite_stamp_fish_id", fishId)
      );

      const caughtButton = card.querySelector("[data-action='toggle-caught']");
      applyCaughtButtonState(
        caughtButton,
        fishName,
        isCaught,
        currentStamp(snapshot, "_caught_stamp_fish_id", fishId),
      );
      state.renderedCardStateByFish.set(fishId, cardStateKey);
    }
  }

  async function toggleFavourite(fishId, owner) {
    if (
      !owner?.currentRepository
      || !Number.isSafeInteger(fishId)
      || fishId <= 0
      || fishId > 4_294_967_295
    ) {
      return null;
    }
    const currentSharedFish = sharedFishSnapshot(owner.currentRepository);
    if (!currentSharedFish) {
      return null;
    }
    const isFavourite = !currentSharedFish.favouriteIds.includes(fishId);
    const projection = await owner.currentRepository.setFavourite(fishId, isFavourite);
    if (projection.kind !== "ready") {
      if (mountIsActive(owner)) {
        showToastOnce(
          `fishydex.repository.favourite.${projection.kind}`,
          "warning",
          repositoryStatusMessage(projection)
            || languageText("fishydex.state.local_storage_unavailable"),
        );
      }
      return projection;
    }
    if (mountIsActive(owner)) {
      state.suppressCardAnimation = true;
      const stampPatch = queueStamp(
        "_favourite_stamp_fish_id",
        isFavourite ? fishId : null,
        owner,
      );
      applyStampPatch(stampPatch, owner);
    }
    return projection;
  }

  function queueFavouriteIntent(fishId, owner = state.mountOwner) {
    return queueDomainIntent(
      (capturedOwner) => toggleFavourite(fishId, capturedOwner),
      owner,
    );
  }

  async function setShowDried(enabled, owner) {
    if (!owner?.currentRepository || typeof enabled !== "boolean") {
      return null;
    }
    const projection = await owner.currentRepository.setShowDried(enabled);
    if (projection.kind !== "ready" && mountIsActive(owner)) {
      showToastOnce(
        `fishydex.repository.show-dried.${projection.kind}`,
        "warning",
        repositoryStatusMessage(projection)
          || languageText("fishydex.state.local_storage_unavailable"),
      );
    }
    return projection;
  }

  async function applyShowDriedIntent(enabled, owner = state.mountOwner) {
    patchPresentation({ show_dried: enabled, _show_dried_pending: true }, owner);
    try {
      return await setShowDried(enabled, owner);
    } finally {
      const authoritative = owner?.currentRepository?.state?.()?.showDried;
      patchPresentation({
        ...(typeof authoritative === "boolean" ? { show_dried: authoritative } : {}),
        _show_dried_pending: false,
      }, owner);
    }
  }

  function bindGridClicks() {
    if (state.gridBound) {
      return;
    }
    const grid = document.getElementById("fishydex-grid");
    if (!(grid instanceof HTMLElement)) {
      return;
    }
    const owner = state.mountOwner;
    listen(grid, "click", function (event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const card = target.closest("[data-fish-id]");
      if (!(card instanceof HTMLElement)) {
        return;
      }
      const fishId = Number.parseInt(card.dataset.fishId || "", 10);
      if (!Number.isInteger(fishId)) {
        return;
      }

      const favouriteButton = target.closest("[data-action='toggle-favourite']");
      if (favouriteButton instanceof HTMLElement) {
        void queueFavouriteIntent(fishId, owner);
        return;
      }

      const caughtButton = target.closest("[data-action='toggle-caught']");
      if (caughtButton instanceof HTMLElement) {
        void queueCaughtIntent(fishId, owner);
        return;
      }

      const openLink = target.closest("[data-action='open-details']")
        || target.closest(".fishydex-card-open")
        || (target.closest("[data-title-hover]") ? card.querySelector(".fishydex-card-open") : null);
      if (!(openLink instanceof HTMLElement) || !isUnmodifiedPrimaryClick(event)) {
        return;
      }
      if (
        (openLink.target && openLink.target !== "_self")
        || (typeof openLink.hasAttribute === "function" && openLink.hasAttribute("download"))
      ) {
        return;
      }
      const meta = fishMetaById(fishId);
      const detailHref = meta ? fishDetailHref(meta) : "";
      const anchorHref = String(openLink.href || "").trim();
      if (
        !meta
        || !detailHref
        || !anchorHref
        || !hrefHasSameResource(anchorHref, detailHref)
      ) {
        return;
      }
      if (pushFishHistory(meta, detailHref)) {
        event.preventDefault();
      }
    }, undefined, owner);
    state.gridBound = true;
  }

  function fishMetaById(fishId) {
    if (!Number.isInteger(fishId)) {
      return null;
    }
    const fish = state.fishById.get(fishId);
    if (!fish) {
      return null;
    }
    const sharedFish = sharedFishSnapshot();
    if (!sharedFish) {
      return null;
    }
    const guideCatchCount = fishGuideCatchCountForEntry(fish);
    const description = fishDescription(fish.description);
    return {
      fishId: fishId,
      itemId: fishItemId(fish),
      iconItemId: fishIconItemId(fish),
      iconImage: fish.icon_image ?? fish.iconImage ?? "",
      encyclopediaId: fishEncyclopediaId(fish),
      encyclopediaIconImage: fishEncyclopediaIconImage(fish),
      name: fishDisplayName(fish, fishId),
      slug: fishSlug(fish),
      description: description?.text || "",
      descriptionSource: description?.source || "",
      grade: filterGradeForEntry(fish),
      isDried: entryIsDried(fish),
      catchMethods: entryCatchMethods(fish),
      vendorPrice: entryVendorPrice(fish),
      caught: guideCatchCount.kind === "known" && guideCatchCount.count > 0,
      guideCatchCount,
      favourite: sharedFish.favouriteIds.includes(fishId),
    };
  }

  function selectedFishId(snapshot) {
    const value = Number(snapshot && snapshot._selected_fish_id);
    return Number.isInteger(value) && value > 0 ? value : 0;
  }

  function selectFishDetails(fishId) {
    const normalizedFishId = Number(fishId);
    const nextFishId = Number.isInteger(normalizedFishId) && normalizedFishId > 0
      ? normalizedFishId
      : 0;
    const owner = state.mountOwner;
    if (!mountIsActive(owner)) return;
    const wasSyncing = state.syncing;
    state.syncing = true;
    try {
      patchPresentation({ _selected_fish_id: nextFishId }, owner);
      if (owner.uiStateRestored && !wasSyncing) renderSelectedFish(owner.presentation, owner);
    } finally {
      state.syncing = wasSyncing;
    }
  }

  function zoneEvidenceStateByItemId(itemId) {
    return Number.isInteger(itemId) && itemId > 0 ? state.zoneEvidenceByFish.get(itemId) || null : null;
  }

  function createZoneEvidenceBadge(label, className, iconName = "") {
    const badge = createElement(
      "span",
      `badge badge-soft ${className}${iconName ? " gap-1" : ""}`.trim(),
    );
    if (!iconName) {
      badge.textContent = label;
      return badge;
    }
    const icon = createElement("span", "inline-flex shrink-0");
    icon.innerHTML = spriteIconMarkup(iconName, "size-4", true);
    badge.append(icon, createElement("span", "", label));
    return badge;
  }

  function populateMapLink(link, label) {
    const icon = createElement("span", "fishydex-map-target");
    icon.innerHTML = ICON_TARGET_FILL;
    const text = createElement("span", "fishydex-map-label", label);
    link.replaceChildren(icon, text);
  }

  function createZoneEvidenceCard(zone, meta) {
    const zoneName = zone.zone_name || languageText("fishydex.details.zones.unknown_zone");
    const rgb = zoneRgbPresentation(zone.zone_rgb);
    const zoneHref = fishydexMapHref(meta.itemId, zone);
    const card = createElement(
      zoneHref ? "a" : "article",
      "fishydex-details-spot rounded-box border border-base-300 bg-base-100",
    );
    if (zoneHref) {
      card.href = zoneHref;
      card.setAttribute(
        "aria-label",
        languageText("fishydex.details.view_on_map", { name: zoneName }),
      );
    }
    const head = createElement("div", "fishydex-details-spot-head");
    const identity = createElement("div", "fishydex-details-spot-identity fishy-zone-row");
    const indicator = createElement("span", "fishy-zone-rgb-indicator");
    indicator.setAttribute("aria-hidden", "true");
    if (rgb) {
      indicator.style.setProperty("--fishy-zone-rgb", rgb.cssValue);
    }
    identity.append(
      indicator,
      createElement("h3", "fishydex-details-spot-name fishy-zone-label", zoneName),
      createElement("span", "fishydex-details-spot-rgb", String(zone.zone_rgb || "")),
    );
    head.appendChild(identity);
    if (zoneHref) {
      const target = createElement("span", "fishydex-details-spot-target");
      target.setAttribute("aria-hidden", "true");
      target.innerHTML = ICON_TARGET_FILL;
      head.appendChild(target);
    }

    const badges = createElement("div", "fishydex-details-spot-badges");
    for (const group of zone.baseline_groups) {
      badges.appendChild(createZoneEvidenceBadge(languageText(
        "fishydex.details.zones.baseline_group",
        { group: languageText(fishingGroupMessageKey(group)) },
      ), fishingGroupBadgeTone(group)));
    }
    for (const group of zone.maintained_groups) {
      badges.appendChild(
        createZoneEvidenceBadge(languageText(
          "fishydex.details.zones.maintained_group",
          { group: languageText(fishingGroupMessageKey(group)) },
        ), "badge-secondary"),
      );
    }
    const fullyContainedCount = zone.ranking_ring_support?.fully_contained_count ?? 0;
    if (fullyContainedCount > 0) {
      badges.appendChild(
        createZoneEvidenceBadge(
          languageText("fishydex.details.zones.ranking_fully_contained", {
            count: fullyContainedCount,
          }),
          "badge-ghost",
          rankingRingIconName("fully_contained"),
        ),
      );
    }
    const partialOverlapCount = zone.ranking_ring_support?.partial_overlap_count ?? 0;
    if (partialOverlapCount > 0) {
      badges.appendChild(
        createZoneEvidenceBadge(
          languageText("fishydex.details.zones.ranking_partial_overlap", {
            count: partialOverlapCount,
          }),
          "badge-ghost",
          rankingRingIconName("partial_overlap"),
        ),
      );
    }

    card.append(head, badges);
    return card;
  }

  function createRankingEvidenceExplanation(iconName, labelKey, bodyKey) {
    const row = createElement("div", "fishydex-details-zone-explainer-row");
    const heading = createElement("div", "fishydex-details-zone-explainer-heading");
    const icon = createElement("span", "inline-flex shrink-0");
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML = spriteIconMarkup(iconName, "size-4", true);
    heading.append(icon, createElement("strong", "", languageText(labelKey)));
    row.append(
      heading,
      createElement("p", "", languageText(bodyKey)),
    );
    return row;
  }

  function createPotentialZoneExplainer() {
    const disclosure = createElement(
      "details",
      "fishydex-details-zone-explainer rounded-box border border-base-300 bg-base-100",
    );
    const summary = createElement("summary", "fishydex-details-zone-explainer-summary");
    const infoIcon = createElement("span", "inline-flex shrink-0");
    infoIcon.setAttribute("aria-hidden", "true");
    infoIcon.innerHTML = spriteIconMarkup("information-fill", "size-4", true);
    summary.append(
      infoIcon,
      createElement("span", "", languageText("fishydex.details.zones.explainer_summary")),
    );
    const body = createElement("div", "fishydex-details-zone-explainer-body");
    body.append(
      createElement(
        "p",
        "fishydex-details-zone-explainer-context",
        languageText("fishydex.details.zones.ranking_context"),
      ),
      createRankingEvidenceExplanation(
        rankingRingIconName("fully_contained"),
        "fishydex.details.zones.fully_contained_label",
        "fishydex.details.zones.fully_contained_explanation",
      ),
      createRankingEvidenceExplanation(
        rankingRingIconName("partial_overlap"),
        "fishydex.details.zones.partial_overlap_label",
        "fishydex.details.zones.partial_overlap_explanation",
      ),
      createElement(
        "p",
        "fishydex-details-zone-explainer-limit",
        languageText("fishydex.details.zones.ranking_limit"),
      ),
    );
    disclosure.append(summary, body);
    return disclosure;
  }

  function createZoneEvidencePanel(kind, zones, meta, active) {
    const panel = createElement("section", "fishydex-details-zone-panel");
    panel.id = `fishydex-details-zone-panel-${kind}`;
    panel.dataset.zoneEvidencePanel = kind;
    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("aria-labelledby", `fishydex-details-zone-tab-${kind}`);
    panel.hidden = !active;
    panel.appendChild(createElement(
      "p",
      "fishydex-details-zone-intro",
      languageText(`fishydex.details.zones.${kind}_intro`),
    ));
    if (kind === "potential") {
      panel.appendChild(createPotentialZoneExplainer());
    }
    const list = createElement("div", "fishydex-details-spot-list");
    for (const zone of zones) {
      list.appendChild(createZoneEvidenceCard(zone, meta));
    }
    panel.appendChild(list);
    return panel;
  }

  function activateZoneEvidenceTab(surface, itemId, kind, focus = false) {
    const controls = surface.__fishystuffZoneEvidenceTabs;
    if (!controls || !controls.buttons.some((button) => button.dataset.zoneEvidenceTab === kind)) {
      return;
    }
    state.zoneEvidenceTabByFish.set(itemId, kind);
    for (const button of controls.buttons) {
      const selected = button.dataset.zoneEvidenceTab === kind;
      button.setAttribute("aria-selected", selected ? "true" : "false");
      button.setAttribute("tabindex", selected ? "0" : "-1");
      button.classList[selected ? "add" : "remove"]("tab-active");
      if (selected && focus) {
        button.focus();
      }
    }
    for (const panel of controls.panels) {
      panel.hidden = panel.dataset.zoneEvidencePanel !== kind;
    }
  }

  function bindZoneEvidenceTabs(surface, itemId, controls = null) {
    let resolved = controls;
    if (!resolved && typeof surface.querySelectorAll === "function") {
      resolved = {
        buttons: Array.from(surface.querySelectorAll("[data-zone-evidence-tab]")),
        panels: Array.from(surface.querySelectorAll("[data-zone-evidence-panel]")),
      };
    }
    if (!resolved || resolved.buttons.length === 0) {
      return;
    }
    surface.__fishystuffZoneEvidenceTabs = resolved;
    const selected = resolved.buttons.find((button) => button.getAttribute("aria-selected") === "true");
    if (selected) {
      state.zoneEvidenceTabByFish.set(itemId, selected.dataset.zoneEvidenceTab);
    }
    for (const button of resolved.buttons) {
      if (button.dataset.zoneEvidenceTabBound === "true") {
        continue;
      }
      button.dataset.zoneEvidenceTabBound = "true";
      listen(button, "click", () => {
        activateZoneEvidenceTab(surface, itemId, button.dataset.zoneEvidenceTab);
      });
      listen(button, "keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
          return;
        }
        event.preventDefault();
        const currentIndex = resolved.buttons.indexOf(button);
        const nextIndex = event.key === "Home"
          ? 0
          : event.key === "End"
            ? resolved.buttons.length - 1
            : (currentIndex + (event.key === "ArrowRight" ? 1 : -1) + resolved.buttons.length)
              % resolved.buttons.length;
        activateZoneEvidenceTab(
          surface,
          itemId,
          resolved.buttons[nextIndex].dataset.zoneEvidenceTab,
          true,
        );
      });
      state.mountOwner?.cleanups.push(() => {
        delete button.dataset.zoneEvidenceTabBound;
      });
    }
  }

  function renderZoneEvidenceStatus(surface, key) {
    const note = createElement("p", "fishydex-details-note");
    note.textContent = languageText(key);
    surface.replaceChildren(note);
  }

  function renderZoneEvidence(surface, meta) {
    if (!(surface instanceof HTMLElement)) {
      return;
    }

    const zoneState = zoneEvidenceStateByItemId(meta.itemId);
    if (!zoneState || zoneState.status === "loading") {
      renderZoneEvidenceStatus(surface, "fishydex.details.zones.loading");
      return;
    }

    if (zoneState.status === "error") {
      const note = createElement("p", "fishydex-details-note is-error");
      note.textContent = zoneState.message || languageText("fishydex.details.zones.error");
      surface.replaceChildren(note);
      return;
    }

    const zones = zoneState.zones;
    if (!zones.length) {
      renderZoneEvidenceStatus(surface, "fishydex.details.zones.empty");
      return;
    }

    const partitioned = partitionZoneEvidence(zones);
    const availableKinds = ZONE_EVIDENCE_KIND_ORDER.filter((kind) => partitioned[kind].length > 0);
    const preferredKind = state.zoneEvidenceTabByFish.get(meta.itemId);
    const activeKind = availableKinds.includes(preferredKind)
      ? preferredKind
      : availableKinds[0];
    const tabList = createElement("div", "fishydex-details-zone-tabs tabs tabs-border tabs-lg sm:tabs-md");
    tabList.setAttribute("role", "tablist");
    tabList.setAttribute("aria-label", languageText("fishydex.details.zones.heading"));
    const buttons = [];
    const panels = [];
    for (const kind of availableKinds) {
      const active = kind === activeKind;
      const button = createElement(
        "button",
        active
          ? "fishydex-details-zone-tab tab tab-active"
          : "fishydex-details-zone-tab tab",
      );
      const icon = createElement("span", "inline-flex shrink-0 mr-2");
      icon.innerHTML = spriteIconMarkup(kind === "known" ? "fullscreen-fill" : "question-mark", "size-4", true);
      button.append(icon, createElement("span", "", languageText(`fishydex.details.zones.tab_${kind}`)));
      button.type = "button";
      button.id = `fishydex-details-zone-tab-${kind}`;
      button.dataset.zoneEvidenceTab = kind;
      button.setAttribute("role", "tab");
      button.setAttribute("aria-controls", `fishydex-details-zone-panel-${kind}`);
      button.setAttribute("aria-selected", active ? "true" : "false");
      button.setAttribute("tabindex", active ? "0" : "-1");
      tabList.appendChild(button);
      buttons.push(button);
      panels.push(createZoneEvidencePanel(kind, partitioned[kind], meta, active));
    }
    surface.replaceChildren(tabList, ...panels);
    bindZoneEvidenceTabs(surface, meta.itemId, { buttons, panels });
  }

  async function ensureZoneEvidence(meta) {
    if (!meta || !Number.isInteger(meta.itemId) || meta.itemId <= 0) {
      return;
    }
    const existing = zoneEvidenceStateByItemId(meta.itemId);
    if (existing && (existing.status === "loading" || existing.status === "ready")) {
      return;
    }

    state.zoneEvidenceByFish.set(meta.itemId, { status: "loading", zones: [] });
    renderDetails();
    const request = {
      generation: state.lifecycleGeneration,
      controller: new AbortController(),
    };
    state.zoneEvidenceRequests.set(meta.itemId, request);

    try {
      const response = await fetch(zoneEvidenceApiUrl(meta.itemId), {
        headers: { Accept: "application/json" },
        signal: request.controller.signal,
      });
      if (!requestIsCurrent(request)) {
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const payload = normalizeZoneEvidenceResponse(
        await response.json(),
        meta.itemId,
        `fish item ${meta.itemId} zone-evidence response`,
      );
      if (!requestIsCurrent(request)) {
        return;
      }
      state.zoneEvidenceByFish.set(meta.itemId, {
        status: "ready",
        zones: payload.zones,
      });
    } catch (error) {
      if (request.controller.signal.aborted || !requestIsCurrent(request)) {
        return;
      }
      state.zoneEvidenceByFish.set(meta.itemId, {
        status: "error",
        message:
          error && error.message
            ? languageText("fishydex.api.zone_evidence_error", { message: error.message })
            : languageText("fishydex.details.zones.error"),
      });
    } finally {
      if (state.zoneEvidenceRequests.get(meta.itemId) === request) {
        state.zoneEvidenceRequests.delete(meta.itemId);
      }
    }

    const presentation = presentationObject();
    if (presentation && selectedFishId(presentation) === meta.fishId) {
      const zoneEvidence = document.getElementById("fishydex-details-zone-evidence");
      applyDataLanguage(zoneEvidence);
      renderZoneEvidence(zoneEvidence, meta);
    }
  }

  function renderDetails(snapshot) {
    const modal = document.getElementById("fishydex-details");
    if (!(modal instanceof HTMLElement)) {
      return;
    }
    const presentation = snapshot && typeof snapshot === "object"
      ? snapshot
      : presentationObject();
    const currentSelectedFishId = selectedFishId(presentation);
    const meta = fishMetaById(currentSelectedFishId);
    if (!meta) {
      // Preserve published details through bootstrap and catalog failure.
      if (currentSelectedFishId > 0 && currentSelectedFishId === state.mountOwner?.publishedFishId) return;
      if (
        !state.detailsOpen
        && modal.hidden
        && !modal.classList.contains("fishydex-details-open")
      ) {
        if (currentSelectedFishId === 0) {
          restoreRootDocumentTitle();
        }
        return;
      }
      modal.classList.remove("fishydex-details-open");
      modal.hidden = true;
      syncDetailsAccessibility(modal, false);
      if (currentSelectedFishId === 0) {
        restoreRootDocumentTitle();
      }
      return;
    }

    state.mountOwner.publishedFishId = 0;
    modal.hidden = false;
    modal.classList.add("fishydex-details-open");
    syncDetailsAccessibility(modal, true);
    setFishDocumentTitle(meta);

    const title = document.getElementById("fishydex-details-title");
    const favouriteToggle = document.getElementById("fishydex-details-favourite-toggle");
    const caughtToggle = document.getElementById("fishydex-details-caught-toggle");
    const favouriteBadge = document.getElementById("fishydex-details-favourite");
    const caughtBadge = document.getElementById("fishydex-details-caught");
    const gradeBadge = document.getElementById("fishydex-details-grade");
    const rodBadge = document.getElementById("fishydex-details-method-rod");
    const hotspotBadge = document.getElementById("fishydex-details-method-hotspot");
    const harpoonBadge = document.getElementById("fishydex-details-method-harpoon");
    const driedBadge = document.getElementById("fishydex-details-dried-badge");
    const itemKey = document.getElementById("fishydex-details-item-key");
    const vendorPrice = document.getElementById("fishydex-details-vendor-price");
    const description = document.getElementById("fishydex-details-description");
    const mapLink = document.getElementById("fishydex-details-map-link");
    const zoneEvidence = document.getElementById("fishydex-details-zone-evidence");
    const iconFrame = document.getElementById("fishydex-details-icon-frame");
    const icon = document.getElementById("fishydex-details-icon");
    const iconPlaceholder = document.getElementById("fishydex-details-placeholder");
    const guideImage = document.getElementById("fishydex-details-guide-image");
    const guidePlaceholder = document.getElementById("fishydex-details-guide-placeholder");

    setElementText(title, meta.name);
    if (title instanceof HTMLElement) {
      title.className = `fishydex-details-title fishy-domain-label fishy-item-label fishy-item-grade-${meta.grade}`;
      applyDataLanguage(title);
    }

    if (favouriteToggle instanceof HTMLButtonElement) {
      favouriteToggle.dataset.favouriteState = meta.favourite ? "active" : "inactive";
      applyFavouriteButtonState(
        favouriteToggle,
        meta.name,
        meta.favourite,
        currentStamp(presentation, "_favourite_stamp_fish_id", meta.fishId)
      );
    }

    if (caughtToggle instanceof HTMLButtonElement) {
      caughtToggle.dataset.caughtState = meta.caught ? "caught" : "uncaught";
      applyCaughtButtonState(
        caughtToggle,
        meta.name,
        meta.caught,
        currentStamp(presentation, "_caught_stamp_fish_id", meta.fishId),
      );
    }

    if (favouriteBadge instanceof HTMLElement) {
      favouriteBadge.hidden = !meta.favourite;
    }
    if (caughtBadge instanceof HTMLElement) {
      caughtBadge.className = meta.caught
        ? "fishydex-caught badge badge-soft badge-success"
        : "fishydex-grade fishy-domain-label badge badge-soft grade-unknown";
      caughtBadge.textContent = meta.caught
        ? languageText("fishydex.details.caught")
        : languageText("fishydex.details.not_caught");
    }
    if (gradeBadge instanceof HTMLElement) {
      gradeBadge.className = `fishydex-grade fishy-domain-label badge badge-soft grade-${meta.grade}`;
      gradeBadge.textContent = gradeLabelForKey(meta.grade);
    }
    if (rodBadge instanceof HTMLElement) {
      rodBadge.hidden = !meta.catchMethods.includes("rod");
    }
    if (hotspotBadge instanceof HTMLElement) {
      hotspotBadge.hidden = !meta.catchMethods.includes("hotspot");
    }
    if (harpoonBadge instanceof HTMLElement) {
      harpoonBadge.hidden = !meta.catchMethods.includes("harpoon");
    }
    if (driedBadge instanceof HTMLElement) {
      driedBadge.hidden = !meta.isDried;
    }

    setElementLink(itemKey, String(meta.itemId), bdolyticsItemUrl(meta.itemId));
    if (vendorPrice instanceof HTMLElement) {
      vendorPrice.className = "fishydex-price fishydex-details-price";
      populateVendorPrice(vendorPrice, meta.vendorPrice);
    }
    if (description instanceof HTMLElement) {
      description.textContent = meta.description
        || languageText("fishydex.details.description_unavailable");
      if (meta.description) {
        description.classList.remove("is-unavailable");
      } else {
        description.classList.add("is-unavailable");
      }
      applyDataLanguage(description);
    }
    if (mapLink instanceof HTMLElement) {
      const mapHref = fishydexMapHref(meta.itemId);
      mapLink.hidden = !mapHref;
      if (mapHref) {
        mapLink.href = mapHref;
        populateMapLink(
          mapLink,
          languageText("fishydex.details.view_on_map", { name: meta.name }),
        );
      }
    }
    applyDataLanguage(zoneEvidence);
    renderZoneEvidence(zoneEvidence, meta);

    if (iconFrame instanceof HTMLElement) {
      // Title controls own the frame's positioning and border-band sizing.
      for (const grade of GRADE_COLOR_ORDER) {
        iconFrame.classList.toggle(`fishy-item-grade-${grade}`, grade === meta.grade);
      }
    }
    if (icon instanceof HTMLImageElement && iconPlaceholder instanceof HTMLElement) {
      icon.className = "fishydex-details-icon fishy-item-icon";
      iconPlaceholder.className = `fishydex-placeholder fishy-item-icon-fallback fishy-item-grade-${meta.grade}`;
      setImageWithPlaceholder(icon, iconPlaceholder, fishItemIconUrl(meta), `${meta.name} icon`);
    }
    if (guideImage instanceof HTMLImageElement && guidePlaceholder instanceof HTMLElement) {
      setImageWithPlaceholder(
        guideImage,
        guidePlaceholder,
        fishEncyclopediaIconUrl(meta.encyclopediaIconImage),
        languageText("fishydex.details.guide_alt", { name: meta.name }),
      );
    }

  }

  function syncDetailsAccessibility(modal, open) {
    modal.setAttribute("aria-hidden", open ? "false" : "true");
    const background = document.getElementById("fishydex-background");
    if (background instanceof HTMLElement) {
      background.inert = open;
      if (open) {
        background.setAttribute("aria-hidden", "true");
      } else {
        background.removeAttribute("aria-hidden");
      }
    }
    if (state.detailsOpen === open) {
      return;
    }
    state.detailsOpen = open;
    if (open) {
      window.requestAnimationFrame(function () {
        const activeTab = modal.querySelector?.("[data-fish-detail-tab][aria-selected='true']");
        if (activeTab && activeTab.dataset.fishDetailTab !== "info") return;
        const title = document.getElementById("fishydex-details-title");
        if (title instanceof HTMLElement) {
          title.focus({ preventScroll: true });
        }
      });
    }
  }

  function trapDetailsFocus(event, modal) {
    if (event.key !== "Tab" || typeof modal.querySelectorAll !== "function") {
      return;
    }
    const focusable = Array.from(modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )).filter(function (element) {
      return element instanceof HTMLElement && !element.hidden;
    });
    if (!focusable.length) {
      event.preventDefault();
      document.getElementById("fishydex-details-title")?.focus({ preventScroll: true });
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function bindHistoryNavigation() {
    if (state.historyBound || typeof window.addEventListener !== "function") {
      return;
    }
    listen(window, "popstate", function (event) {
      const historyFishId = ownedHistoryFishId(event && event.state);
      if (historyFishId > 0) {
        selectFishDetails(historyFishId);
        return;
      }
      if (fishydexRootIsCurrentResource()) {
        selectFishDetails(0);
        restoreRootDocumentTitle();
      }
    });
    state.historyBound = true;
  }

  function closeDetails() {
    const returnHref = currentHistoryFishId() > 0
      ? window.history.state[FISHYDEX_HISTORY_STATE_KEY].returnHref : null;
    if (returnHref) {
      const nextState = { ...window.history.state };
      delete nextState[FISHYDEX_HISTORY_STATE_KEY];
      window.history.replaceState(nextState, "", returnHref);
      selectFishDetails(0);
      restoreRootDocumentTitle();
      return;
    }
    if (
      currentHistoryFishId() > 0
      && window.history
      && typeof window.history.back === "function"
    ) {
      window.history.back();
      return;
    }
    selectFishDetails(0);
  }

  function bindDetailsControls() {
    if (state.detailsBound) {
      return;
    }
    const modal = document.getElementById("fishydex-details");
    if (!(modal instanceof HTMLElement)) {
      return;
    }
    const owner = state.mountOwner;
    listen(modal, "click", function (event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const closeControl = target.closest("[data-action='close-details']");
      if (closeControl && isUnmodifiedPrimaryClick(event)) {
        event.preventDefault();
        closeDetails();
        return;
      }
      if (target.closest("[data-action='toggle-favourite-details']")) {
        const presentation = presentationObject();
        const fishId = selectedFishId(presentation);
        if (Number.isInteger(fishId) && fishId > 0) {
          void queueFavouriteIntent(fishId, owner);
        }
        return;
      }
      if (target.closest("[data-action='toggle-caught-details']")) {
        const presentation = presentationObject();
        const fishId = selectedFishId(presentation);
        if (Number.isInteger(fishId) && fishId > 0) {
          void queueCaughtIntent(fishId, owner);
        }
      }
    }, undefined, owner);
    listen(document, "keydown", function (event) {
      const presentation = presentationObject();
      if (event.key === "Escape" && selectedFishId(presentation) > 0) {
        event.preventDefault();
        closeDetails();
        return;
      }
      if (selectedFishId(presentation) > 0) {
        trapDetailsFocus(event, modal);
      }
    }, undefined, owner);
    state.detailsBound = true;
  }

  function syncChangedProgress(owner, previous, projection, changes) {
    if (owner.presentation.search_query) { sync(owner.presentation); return; }
    const changed = new Set([...changes.countFishIds, ...changes.favouriteFishIds]);
    if (!changed.size) return;
    const snapshot = owner.presentation;
    const caught = new Set();
    const patch = {};
    let membershipChanged = false;
    for (const id of changed) {
      const entry = state.fishById.get(id);
      if (!entry) continue;
      const before = fishGuideCatchCountForEntry(entry, previous);
      const after = fishGuideCatchCountForEntry(entry, projection);
      const wasCaught = before.kind === "known" && before.count > 0;
      const isCaught = after.kind === "known" && after.count > 0;
      if (isCaught) caught.add(id);
      const delta = Number(isCaught) - Number(wasCaught);
      if (catalogKind(entry) === "item") continue;
      if (!delta || (!snapshot.show_dried && entryIsDried(entry))) continue;
      membershipChanged = true;
      patch.caught_count = (patch.caught_count ?? snapshot.caught_count) + delta;
      const grade = filterGradeForEntry(entry);
      const field = `${grade}_caught_count`;
      if (Object.hasOwn(snapshot, field)) patch[field] = (patch[field] ?? snapshot[field]) + delta;
    }
    if (patch.caught_count !== undefined) {
      patch.completion_percent = completionPercent(patch.caught_count, snapshot.fish_count);
      for (const grade of GRADE_COLOR_ORDER) {
        if (patch[`${grade}_caught_count`] !== undefined) {
          patch[`${grade}_completion_percent`] = completionPercent(
            patch[`${grade}_caught_count`], snapshot[`${grade}_total_count`],
          );
        }
      }
    }
    state.syncing = true;
    try {
      syncRenderedCardState(snapshot, caught, new Set(projection.favouriteIds), changed);
      if (Object.keys(patch).length) patchPresentation(patch, owner);
      if (changed.has(selectedFishId(snapshot))) renderDetailsCount(snapshot);
      const selectedId = selectedFishId(snapshot);
      if (changes.favouriteFishIds.has(selectedId)) {
        const badge = document.getElementById("fishydex-details-favourite");
        if (badge) badge.hidden = !projection.favouriteIds.includes(selectedId);
        const entry = state.fishById.get(selectedId);
        if (entry) applyFavouriteButtonState(
          document.getElementById("fishydex-details-favourite-toggle"),
          fishDisplayName(entry, selectedId), projection.favouriteIds.includes(selectedId),
          currentStamp(snapshot, "_favourite_stamp_fish_id", selectedId),
        );
      }
    } finally {
      state.syncing = false;
    }
  }

  function renderDetailsCount(snapshot) {
    const entry = state.fishById.get(selectedFishId(snapshot));
    if (!entry) return;
    const count = fishGuideCatchCountForEntry(entry);
    const caught = count.kind === "known" && count.count > 0;
    const button = document.getElementById("fishydex-details-caught-toggle");
    if (button instanceof HTMLButtonElement) {
      if (button.dataset.caughtState !== (caught ? "caught" : "uncaught")) {
        button.dataset.caughtState = caught ? "caught" : "uncaught";
      }
      applyCaughtButtonState(button, fishDisplayName(entry, fishItemId(entry)), caught,
        currentStamp(snapshot, "_caught_stamp_fish_id", fishItemId(entry)));
    }
    const badge = document.getElementById("fishydex-details-caught");
    if (badge) {
      badge.className = caught ? "fishydex-caught badge badge-soft badge-success"
        : "fishydex-grade fishy-domain-label badge badge-soft grade-unknown";
      setElementText(badge, languageText(caught ? "fishydex.details.caught" : "fishydex.details.not_caught"));
    }
  }

  function sync(snapshot, options = {}) {
    if (state.syncing) {
      return;
    }
    state.syncing = true;
    try {
      bindGridClicks();
      bindDetailsControls();

      const sourceFish = Array.isArray(snapshot.fish) ? snapshot.fish : [];
      if (options.catalogUpdated) {
        try {
          validateCatalogCatchMethods(sourceFish);
          state.catalogContractError = null;
        } catch (error) {
          state.catalogContractError = error instanceof Error
            ? error
            : new Error(String(error || "unknown Fishydex catalog error"));
        }
      }
      const catalogContractError = state.catalogContractError;
      const fish = catalogContractError ? [] : sourceFish;
      const sharedFish = sharedFishSnapshot(snapshot);
      const owner = state.mountOwner;
      const queryUnavailable = !usesAuthoredCatalogPreferences(snapshot) && (
        !owner.uiStateRestored
        || !owner.uiWritable
        || !sharedFish
        || owner.currentProjection?.fishGuideProgress == null
        || (fish.length === 0 && snapshot._catalog_state === "pending")
      );
      if (snapshot._query_unavailable !== queryUnavailable) {
        patchPresentation({ _query_unavailable: queryUnavailable }, owner);
      }
      if (queryUnavailable || !sharedFish || state.fishGuideProgress === null) {
        owner.detailNavigation?.setCatalog({ kind: "unavailable" });
        return;
      }
      const caughtIds = effectiveCaughtIds(fish);
      const favouriteIds = sharedFish.favouriteIds;
      const showDried = normalizeBooleanFlag(snapshot.show_dried);
      const showNonFish = snapshot.show_non_fish;
      const sortField = normalizeSortField(snapshot.sort_field);
      const sortDirection = normalizeSortDirection(snapshot.sort_direction);
      const requestedCatalogView = normalizeCatalogView(snapshot.catalog_view);
      const searchQuery = normalizeSearchQuery(snapshot.search_query);
      const catalogState = catalogContractError
        ? "unavailable"
        : fish.length > 0
        ? "ready"
        : normalizeCatalogState(snapshot._catalog_state);
      const catalogIsPending = catalogState === "pending";

      state.fishById = new Map(
        fish
          .filter(function (entry) {
            return entry && fishItemId(entry) > 0;
          })
          .map(function (entry) {
            return [fishItemId(entry), entry];
          })
      );

      const query = state.mountOwner.query.select(searchQuery, {
        fish, includeNonFish: showNonFish,
        revision: snapshot.revision, locale: snapshot.lang.locale,
        progress: state.currentProjection || {}, guideCount: guideCountForEntry,
        caughtIds: new Set(caughtIds), favouriteIds: new Set(favouriteIds), grade: filterGradeForEntry,
      });
      const catalogEntries = selectCatalogItems(fish, {
        includeNonFish: showNonFish, includeDried: showDried || query.includesDried,
      });
      const progressEntries = selectFishProgressItems(catalogEntries);
      const filtered = catalogEntries.filter(function (entry) {
        if (!query.matches(entry)) return false;
        return true;
      });

      const supportsGradeFilter = fish.some(function (entry) {
        return entry && (entry.is_prize !== null || entry.grade);
      });
      const supportsDriedFilter = fish.some(function (entry) {
        return entry && entryIsDried(entry);
      });
      const supportsGuideView = fish.some(function (entry) {
        return entry && fishGuideOrderForEntry(entry) !== null;
      });
      const catalogSupportKnown = fish.length > 0;
      const supportsGradeFilterState = catalogSupportKnown
        ? supportsGradeFilter
        : normalizeBooleanFlag(snapshot.supports_grade_filter);
      const supportsDriedFilterState = catalogSupportKnown
        ? supportsDriedFilter
        : normalizeBooleanFlag(snapshot.supports_dried_filter);
      const supportsGuideViewState = catalogSupportKnown
        ? supportsGuideView
        : normalizeBooleanFlag(snapshot.supports_guide_view) || requestedCatalogView === "guide";
      const catalogView = catalogSupportKnown && requestedCatalogView === "guide" && !supportsGuideView
        ? "grade"
        : requestedCatalogView;
      const sortedEntries = catalogView === "guide"
        ? filtered.slice().sort(compareFishGuideEntries)
        : filtered.slice().sort(function (left, right) {
          return compareFishEntries(left, right, sortField, sortDirection);
        });

      const gradeProgress = {
        red: { total: 0, caught: 0 },
        yellow: { total: 0, caught: 0 },
        blue: { total: 0, caught: 0 },
        green: { total: 0, caught: 0 },
        white: { total: 0, caught: 0 },
      };
      for (const entry of progressEntries) {
        const grade = filterGradeForEntry(entry);
        if (!gradeProgress[grade]) {
          continue;
        }
        gradeProgress[grade].total += 1;
        if (caughtIds.includes(fishItemId(entry))) {
          gradeProgress[grade].caught += 1;
        }
      }

      const cardContentKey = [
        snapshot.revision || "",
        fishRenderSignature(fish),
        fishGuideProgressSignature(state.fishGuideProgress),
        currentLanguage().locale,
        currentDataHtmlLang(),
      ].join("|");
      const renderKey = [
        cardContentKey,
        Boolean(searchQuery),
        searchQuery ? filtered.map(fishItemId).join(",") : "",
        showDried ? "1" : "0",
        showNonFish ? "1" : "0",
        catalogView,
        sortField,
        sortDirection,
      ].join("|");

      if (renderKey !== state.renderKey) {
        state.renderKey = renderKey;
        const grid = document.getElementById("fishydex-grid");
        if (grid instanceof HTMLElement) {
          if (cardContentKey !== state.cardContentKey) {
            state.cardContentKey = cardContentKey;
            state.renderedCards.clear();
            state.renderedCardStateByFish.clear();
          }
          const preserveAuthoredCatalog = catalogIsPending
            && usesAuthoredCatalogPreferences(snapshot)
            && grid.dataset.fishydexAuthoredCatalog === "true";
          const hydrateAuthoredCatalogInPlace =
            catalogState === "ready"
            && grid.dataset.fishydexAuthoredCatalog === "true"
            && Number.parseInt(grid.dataset.fishydexAuthoredCount || "", 10)
              === catalogEntries.length
            && usesAuthoredCatalogPreferences(snapshot);
          if (preserveAuthoredCatalog) {
            state.suppressCardAnimation = true;
          } else if (
            hydrateAuthoredCatalogInPlace
            && hydrateAuthoredCatalog(grid, fish, caughtIds, favouriteIds, snapshot)
          ) {
            state.suppressCardAnimation = true;
          } else {
            cancelAuthoredCatalogHydration();
            const animateCards = !state.suppressCardAnimation;
            const groupDefinitions = catalogView === "simple"
              ? [{ key: "simple:all", label: languageText("fishydex.group.all"), match: () => true }]
              : catalogView === "guide"
              ? FISH_GUIDE_CATEGORY_ORDER.map(function (category) {
                return {
                  key: `guide:${category}`,
                  label: fishGuideCategoryLabel(category),
                  match(entry) {
                    return fishGuideCategoryForEntry(entry) === category;
                  },
                };
              })
              : GRADE_COLOR_ORDER.map(function (grade) {
                return {
                  key: `grade:${grade}`,
                  label: gradeLabelForKey(grade),
                  match(entry) {
                    return filterGradeForEntry(entry) === grade;
                  },
                };
              });
            const caughtSet = new Set(caughtIds);
            const favouriteSet = new Set(favouriteIds);
            reconcileFishydexCatalog({
              grid,
              groups: groupDefinitions.map(function (group) {
                const entries = sortedEntries.filter(group.match);
                return {
                  key: group.key,
                  label: group.label,
                  countLabel: languageText("fishydex.group.count", { count: entries.length }),
                  entries,
                };
              }),
              cards: state.renderedCards,
              itemId: fishItemId,
              createElement,
              renderCard: (entry, index) => renderFishCard(
                entry, caughtSet, favouriteSet, snapshot, index, animateCards,
              ),
              renderEmpty: catalogIsPending ? null : () => renderEmptyState(Boolean(searchQuery)),
            });
            grid.dataset.fishydexCatalogState = catalogState;
            delete grid.dataset.fishydexAuthoredCatalog;
            delete grid.dataset.fishydexAuthoredCount;
            state.suppressCardAnimation = false;
          }
        }
      }
      syncRenderedCardState(snapshot, new Set(caughtIds), new Set(favouriteIds));

      const caughtCount = progressEntries.reduce(function (count, entry) {
        return count + (caughtIds.includes(fishItemId(entry)) ? 1 : 0);
      }, 0);

      patchPresentation({
        ...(catalogContractError ? { fish: [], count: 0 } : {}),
        catalog_count: catalogEntries.length,
        fish_count: progressEntries.length,
        visible_count: sortedEntries.length,
        caught_count: caughtCount,
        completion_percent: completionPercent(caughtCount, progressEntries.length),
        red_total_count: gradeProgress.red.total,
        red_caught_count: gradeProgress.red.caught,
        red_completion_percent: completionPercent(gradeProgress.red.caught, gradeProgress.red.total),
        yellow_total_count: gradeProgress.yellow.total,
        yellow_caught_count: gradeProgress.yellow.caught,
        yellow_completion_percent: completionPercent(gradeProgress.yellow.caught, gradeProgress.yellow.total),
        blue_total_count: gradeProgress.blue.total,
        blue_caught_count: gradeProgress.blue.caught,
        blue_completion_percent: completionPercent(gradeProgress.blue.caught, gradeProgress.blue.total),
        green_total_count: gradeProgress.green.total,
        green_caught_count: gradeProgress.green.caught,
        green_completion_percent: completionPercent(gradeProgress.green.caught, gradeProgress.green.total),
        white_total_count: gradeProgress.white.total,
        white_caught_count: gradeProgress.white.caught,
        white_completion_percent: completionPercent(gradeProgress.white.caught, gradeProgress.white.total),
        supports_grade_filter: supportsGradeFilterState,
        supports_dried_filter: supportsDriedFilterState,
        supports_guide_view: supportsGuideViewState,
        catalog_view: catalogView,
        _catalog_state: catalogState,
        _api_error_message: catalogContractError
          ? languageText("fishydex.api.contract_error", {
            detail: catalogContractError.message,
          })
          : fish.length > 0 ? "" : snapshot._api_error_message,
        _api_error_hint: catalogContractError
          ? ""
          : fish.length > 0 ? "" : snapshot._api_error_hint,
      });
      query.applied();
      owner.detailNavigation?.setCatalog(catalogState === "ready"
        ? { kind: "ready", entries: readFishDetailSequence(document.getElementById("fishydex-grid")) }
        : { kind: catalogIsPending ? "loading" : "unavailable" });
      renderSelectedFish(snapshot, owner);
    } finally {
      state.syncing = false;
    }
  }

  function renderSelectedFish(snapshot, owner) {
    owner.detailNavigation?.select(selectedFishId(snapshot));
    if (snapshot.revision) {
      owner.titleControls?.update({
        fish: snapshot.fish,
        selectedFish: state.fishById.get(selectedFishId(snapshot)) || null,
        lang: currentLanguage().apiLang,
        revision: snapshot.revision,
        htmlLang: currentDataHtmlLang(),
        locale: currentLanguage().locale,
      });
    }
    renderDetails(snapshot);
    const meta = fishMetaById(selectedFishId(snapshot));
    if (meta) void ensureZoneEvidence(meta);
  }

  function showToast(tone, message, options = {}) {
    const toast = window.__fishystuffToast;
    if (!toast || !message) {
      return;
    }
    const extra = options && typeof options === "object" ? options : {};
    const handler = typeof toast[tone] === "function"
      ? function (value, config) {
          return toast[tone](value, config);
        }
      : typeof toast.show === "function"
        ? function (value, config) {
            return toast.show({
              tone: tone,
              message: value,
              ...(config || {}),
            });
          }
        : null;
    if (handler) {
      return handler(message, extra);
    }
    return null;
  }

  function showToastOnce(key, tone, message, options) {
    if (!message) {
      return null;
    }
    if (key && state.toastKeys.has(key)) {
      return null;
    }
    const result = showToast(tone, message, options);
    if (key && result) {
      state.toastKeys.add(key);
    }
    return result;
  }

  function apiUrl(pathname) {
    if (typeof window.__fishystuffResolveApiUrl === "function") {
      return window.__fishystuffResolveApiUrl(pathname);
    }
    const candidates = [
      window.__fishystuffApiBaseUrl,
      window.__fishystuffRuntimeConfig && window.__fishystuffRuntimeConfig.apiBaseUrl,
      fallbackPublicBaseUrl("api", "https://api.fishystuff.fish", "8080"),
    ];
    for (const value of candidates) {
      const explicitBase = String(value || "").trim();
      if (explicitBase) {
        return new URL(pathname, explicitBase).toString();
      }
    }
    return new URL(pathname, "https://api.fishystuff.fish").toString();
  }

  function fishApiUrl() {
    return apiUrl(`/api/v1/fish?lang=${encodeURIComponent(currentLanguage().apiLang)}`);
  }

  function zoneEvidenceApiUrl(itemId) {
    return apiUrl(`/api/v1/fish/${itemId}/zones?lang=${encodeURIComponent(currentLanguage().apiLang)}`);
  }

  function normalizeCatalogResponse(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      throw new TypeError("Fishydex catalog response must be an object");
    }
    const keys = Object.keys(value).sort();
    const expectedKeys = ["count", "fish", "revision"];
    if (
      keys.length !== expectedKeys.length
      || keys.some(function (key, index) { return key !== expectedKeys[index]; })
    ) {
      throw new TypeError("Fishydex catalog response has unsupported or missing fields");
    }
    if (
      typeof value.revision !== "string"
      || !value.revision
      || value.revision !== value.revision.trim()
      || value.revision.length > 256
      || /[\u0000-\u001f\u007f]/.test(value.revision)
    ) {
      throw new TypeError("Fishydex catalog response revision must be bounded canonical text");
    }
    if (
      !Number.isSafeInteger(value.count)
      || value.count < 0
      || value.count > MAX_FISHYDEX_CATALOG_ITEMS
    ) {
      throw new TypeError("Fishydex catalog response count exceeds its bounded safe-integer contract");
    }
    if (!Array.isArray(value.fish) || value.fish.length !== value.count) {
      throw new TypeError("Fishydex catalog response fish must match count");
    }
    return {
      revision: value.revision,
      count: value.count,
      fish: value.fish,
    };
  }

  async function loadCatalog() {
    const presentation = presentationObject();
    if (!presentation) {
      return false;
    }
    state.catalogAbortController?.controller.abort();
    const request = {
      generation: state.lifecycleGeneration,
      controller: new AbortController(),
    };
    state.catalogAbortController = request;
    try {
      await languageReady();
      if (!requestIsCurrent(request)) {
        return false;
      }
      const response = await fetch(fishApiUrl(), {
        headers: { Accept: "application/json" },
        signal: request.controller.signal,
      });
      if (!requestIsCurrent(request)) {
        return false;
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const catalog = normalizeCatalogResponse(await response.json());
      if (!requestIsCurrent(request)) {
        return false;
      }
      patchPresentation({
        ...catalog,
        _loading: false,
        _catalog_state: "ready",
        _api_error_message: "",
        _api_error_hint: "",
      });
      sync(presentation, { catalogUpdated: true });
      return true;
    } catch (error) {
      if (request.controller.signal.aborted || !requestIsCurrent(request)) {
        return false;
      }
      const status = String(error?.message || "").match(/^HTTP (\d+)$/)?.[1];
      patchPresentation({
        fish: [],
        count: 0,
        _loading: false,
        _catalog_state: "unavailable",
        _api_error_message: status
          ? languageText("fishydex.api.fish_error_status", { status })
          : languageText("fishydex.api.fish_error"),
        _api_error_hint: languageText("fishydex.api.cors_hint"),
      });
      sync(presentation, { catalogUpdated: true });
      return false;
    } finally {
      if (state.catalogAbortController === request) {
        state.catalogAbortController = null;
      }
    }
  }

  const fishydexApi = Object.freeze({
    mount: mount,
    ready: languageReady,
    loadCatalog: loadCatalog,
    fishApiUrl: fishApiUrl,
    dexPresetCollectionKey: FISHYDEX_PRESET_COLLECTION_KEY,
  });

  const mountedSurfaces = new WeakMap();

  function mountConnectedSurface(surface) {
    if (
      !(surface instanceof HTMLElement)
      || typeof surface.applyProjection !== "function"
      || mountedSurfaces.has(surface)
    ) {
      return null;
    }
    const controller = mount(surface);
    mountedSurfaces.set(surface, controller);
    const disconnected = () => {
      if (mountedSurfaces.get(surface) === controller) {
        mountedSurfaces.delete(surface);
      }
      controller.dispose();
    };
    surface.addEventListener("fishydex-surface-disconnected", disconnected, { once: true });
    void controller.ready.then(() => (
      mountedSurfaces.get(surface) === controller ? loadCatalog() : false
    ));
    return controller;
  }

  document.addEventListener("fishydex-surface-connected", (event) => {
    mountConnectedSurface(event.target);
  });
  for (const surface of document.querySelectorAll("fishydex-surface")) {
    mountConnectedSurface(surface);
  }
})();
